import { useState, useMemo, useEffect, useLayoutEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "../../contexts/ToastContext";
import {
  useVisibilityRefresh,
  useCombinedRefetch,
  DEFAULT_POLLING_INTERVAL,
} from "../../hooks/useRealtimeQuery";
import {
  selectTherapistProfile,
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectIsAdmin,
  selectHasPermission,
  selectClinicProfile,
} from "../../store/slices/authSlice";
import {
  useGetTasksQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useReorderTasksMutation,
  useDeleteTaskMutation,
  useGetRemindersQuery,
  useCreateReminderMutation,
  useUpdateReminderMutation,
  useDeleteReminderMutation,
  useGetCategoriesQuery,
} from "../../store/services/tasksApi";
import {
  useGetFormsQuery,
  useCreateFormMutation,
  useDeleteFormMutation,
  useDuplicateFormMutation,
  useGetTemplatesQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  useDuplicateTemplateMutation,
  useSendFormForSignatureMutation,
  useGetPendingSignatureLinksQuery,
  useCancelSignatureLinkMutation,
  useDeleteSignatureLinkMutation,
} from "../../store/services/formsApi";
import {
  useGetQuestionnairesQuery,
  useCreateQuestionnaireMutation,
  useUpdateQuestionnaireMutation,
  useDeleteQuestionnaireMutation,
  useDuplicateQuestionnaireMutation,
  useSendQuestionnaireToPatientsMutation,
  useGetPendingQuestionnaireLinksQuery,
} from "../../store/services/questionnairesApi";
import { useGetPatientsQuery, PATIENTS_PICKER_PAGE_SIZE } from "../../store/services/patientsApi";
import RichTextEditor from "../../components/ui/RichTextEditor";
import useHandleReorder from "../../hooks/useHandleReorder";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import DatePicker from "../../components/ui/DatePicker";
import TimePicker from "../../components/ui/TimePicker";
import {
  ListChecks,
  Plus,
  Check,
  Clock,
  Hourglass,
  CircleDashed,
  X,
  Pencil,
  Trash,
  CalendarBlank,
  ClockCounterClockwise,
  Note,
  CaretDown,
  CaretUp,
  Tag,
  Buildings,
  ChatCircleText,
  UserPlus,
  Phone,
  Eye,
  Info,
  // Forms page icons
  Toolbox,
  ClipboardText,
  Copy,
  MagnifyingGlass,
  User,
  TextAa,
  TextB,
  Palette,
  TextHOne,
  TextHTwo,
  TextT,
  PencilLine,
  Signature,
  ArrowLeft,
  TextAlignLeft,
  TextAlignJustify,
  CheckSquare,
  CaretCircleDown,
  RadioButton,
  DotsSixVertical,
  Asterisk,
  FileText,
  GridFour,
  Microphone,
  PaperPlaneTilt,
  IdentificationCard,
  Warning,
  Link as LinkIcon,
  ListBullets,
  UsersThree,
  CheckCircle,
  Users,
  FolderSimple,
  Star,
} from "@phosphor-icons/react";
import { HelpButton, PageHeader } from "../../components/shared";
import ContentLoader from "../../components/shared/ContentLoader";
import LoaderLoop from "../../components/shared/loader/LoaderLoop";
import ClinicFilesTab from "../../components/clinic/ClinicFilesTab";
import LeadsTab from "../../components/leads/LeadsTab";
import ModalCloseButton from "../../components/ui/ModalCloseButton";
import TabBar from "../../components/ui/TabBar";
import { sortPatientsByName } from "../../utils/patientSortUtils";
import CustomClinikitActionButton from "../../components/ui/CustomClinikitActionButton";
import EmptyDraw from "../../components/ui/EmptyDraw";
import { countSignatureSlots } from "../../components/forms/formPlaceholders";
import HoverTip from "../../components/ui/HoverTip";
import SystemContentDisclaimer from "../../components/shared/SystemContentDisclaimer";
import {
  getSystemForms,
  getSystemTemplates,
  getSystemQuestionnaires,
} from "../../data/systemContent";
import "./TasksRemindersPage.css";
import { getPlaceholderTemplate } from "../../utils/smsTemplates";

// Status configuration - UI order (dropdowns, filters)
const TASK_STATUSES = [
  { value: "todo", label: "פתוח", icon: CircleDashed, color: "#4dd9b4" },
  { value: "in_progress", label: "בתהליך", icon: Hourglass, color: "#3b82f6" },
  { value: "waiting", label: "ממתין", icon: Clock, color: "#f59e0b" },
  { value: "done", label: "הסתיים", icon: Check, color: "#22c55e" },
];

// Format date for display. When withTime is true, append HH:mm.
const formatDate = (dateStr, withTime = false) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const datePart = date.toLocaleDateString("he-IL", {
    day: "numeric",
    month: "short",
  });
  if (!withTime) return datePart;
  const timePart = date.toLocaleTimeString("he-IL", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${datePart}, ${timePart}`;
};

// Extract local "YYYY-MM-DD" from a Firestore-serialized ISO date string.
// Task due dates are built in local time (see buildTaskDueAt), so splitting
// the UTC ISO string on "T" would shift the day back for Israel users.
const extractDateFromIso = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Extract "HH:mm" from a Firestore-serialized ISO date string.
const extractTimeFromIso = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

// Format datetime for history
const formatDateTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("he-IL", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Check if a task is past its deadline.
// With a precise time (hasDueTime), compare against now.
// Date-only deadlines stay end-of-day forgiving (past = before today).
const isDueDatePast = (dateStr, hasDueTime = false) => {
  if (!dateStr) return false;
  const dueDate = new Date(dateStr);
  if (hasDueTime) {
    return dueDate.getTime() <= Date.now();
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dueDate < today;
};

// =====================================================
// FORMS PAGE CONSTANTS AND HELPERS
// =====================================================

// Default SMS template for signature requests. Sourced from the shared SMS
// catalog so the text the therapist sees here and the text the server sends
// cannot drift; the server swaps in the English variant per patient when the
// therapist left this default untouched.
const DEFAULT_SIGNATURE_SMS_TEMPLATE = getPlaceholderTemplate(
  "signature.request",
  "he"
);

// Field type configuration
const FIELD_TYPES = [
  { value: "short_text", label: "טקסט קצר", icon: TextAlignLeft },
  { value: "long_text", label: "טקסט ארוך", icon: TextAlignJustify },
  { value: "date", label: "תאריך", icon: CalendarBlank },
  { value: "checkbox", label: "תיבת סימון", icon: CheckSquare },
  { value: "radio", label: "בחירה מתוך אפשרויות", icon: RadioButton },
  { value: "multi_select", label: "בחירה מרובה", icon: ListChecks },
  { value: "section_header", label: "כותרת (הפרדה בין שדות)", icon: TextHOne },
];


// Generate unique field ID
// Read the current translateY applied to an element (0 if none) — used by the
// task drag FLIP to separate a card's layout slot from its visual position.
const getTranslateY = (el) => {
  const t = window.getComputedStyle(el).transform;
  if (!t || t === "none") return 0;
  const m = t.match(/matrix\(([^)]+)\)/);
  return m ? parseFloat(m[1].split(",")[5]) || 0 : 0;
};

const generateFieldId = () => {
  return `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Available patient fields for placeholders
const PATIENT_FIELD_PLACEHOLDERS = [
  { field: "firstName", label: "שם פרטי", placeholder: "{שם פרטי}", group: "פרטי מטופל" },
  { field: "lastName", label: "שם משפחה", placeholder: "{שם משפחה}", group: "פרטי מטופל" },
  { field: "fullName", label: "שם מלא", placeholder: "{שם מלא}", group: "פרטי מטופל" },
  { field: "phone", label: "טלפון", placeholder: "{טלפון}", group: "פרטי מטופל" },
  { field: "idNumber", label: "תעודת זהות", placeholder: "{תעודת זהות}", group: "פרטי מטופל" },
  { field: "email", label: "אימייל", placeholder: "{אימייל}", group: "פרטי מטופל" },
  { field: "birthDate", label: "תאריך לידה", placeholder: "{תאריך לידה}", group: "פרטי מטופל" },
  { field: "address", label: "כתובת", placeholder: "{כתובת}", group: "פרטי מטופל" },
  { field: "city", label: "עיר", placeholder: "{עיר}", group: "פרטי מטופל" },
  { field: "clinicName", label: "שם הקליניקה", placeholder: "{שם הקליניקה}", group: "קליניקה" },
  { field: "parent1Name", label: "הורה 1 - שם", placeholder: "{הורה 1 - שם}", group: "הורים" },
  { field: "parent1Phone", label: "הורה 1 - טלפון", placeholder: "{הורה 1 - טלפון}", group: "הורים" },
  { field: "parent1Id", label: "הורה 1 - תעודת זהות", placeholder: "{הורה 1 - תעודת זהות}", group: "הורים" },
  { field: "parent2Name", label: "הורה 2 - שם", placeholder: "{הורה 2 - שם}", group: "הורים" },
  { field: "parent2Phone", label: "הורה 2 - טלפון", placeholder: "{הורה 2 - טלפון}", group: "הורים" },
  { field: "parent2Id", label: "הורה 2 - תעודת זהות", placeholder: "{הורה 2 - תעודת זהות}", group: "הורים" },
  { field: "emergencyContactName", label: "איש קשר חירום - שם", placeholder: "{איש קשר חירום - שם}", group: "איש קשר חירום" },
  { field: "emergencyContactRelation", label: "איש קשר חירום - יחס", placeholder: "{איש קשר חירום - יחס}", group: "איש קשר חירום" },
  { field: "emergencyContactPhone", label: "איש קשר חירום - טלפון", placeholder: "{איש קשר חירום - טלפון}", group: "איש קשר חירום" },
];

// Questionnaire field types (subset of template field types)
const QUESTIONNAIRE_FIELD_TYPES = [
  { value: "short_text", label: "טקסט קצר", icon: TextAlignLeft },
  { value: "long_text", label: "טקסט ארוך", icon: TextAlignJustify },
  { value: "date", label: "תאריך", icon: CalendarBlank },
  { value: "checkbox", label: "תיבת סימון", icon: CheckSquare },
  { value: "select", label: "בחירה מרשימה נפתחת", icon: CaretCircleDown },
  { value: "radio", label: "בחירה מתוך אפשרויות", icon: RadioButton },
  { value: "multi_select", label: "בחירה מרובה", icon: ListChecks },
  { value: "section_header", label: "כותרת (הפרדה בין שאלות)", icon: TextHOne },
];

// Field types where the therapist pre-defines answer options
const CHOICE_FIELD_TYPES = ["select", "radio", "multi_select"];

const isChoiceFieldType = (type) => CHOICE_FIELD_TYPES.includes(type);

// A choice field is valid only with at least 2 non-empty options
const questionnaireFieldsHaveInvalidChoices = (fields) =>
  fields.some(
    (field) =>
      isChoiceFieldType(field.type) &&
      (field.options || []).filter((opt) => opt.trim()).length < 2
  );

// Trim options and drop empty ones before saving
const sanitizeQuestionnaireFields = (fields) =>
  fields.map((field) => {
    if (!isChoiceFieldType(field.type)) return field;
    return { ...field, options: (field.options || []).map((opt) => opt.trim()).filter(Boolean) };
  });

// Helper function to extract patient field placeholders from form content
const extractPatientFieldPlaceholders = (htmlContent) => {
  const placeholders = [];
  PATIENT_FIELD_PLACEHOLDERS.forEach(({ field, placeholder }) => {
    if (htmlContent && htmlContent.includes(`data-field="${field}"`)) {
      placeholders.push({ field, placeholder });
    }
  });
  return placeholders;
};

// Helper to resolve a patient/context field value by field key
const resolveFieldValue = (field, patient, extraData = {}) => {
  if (field === "fullName") {
    return `${patient.firstName || ""} ${patient.lastName || ""}`.trim();
  }
  if (field === "birthDate" && patient.birthDate) {
    const date = new Date(patient.birthDate);
    return date.toLocaleDateString("he-IL", { day: "numeric", month: "long", year: "numeric" });
  }
  if (field === "clinicName") return extraData.clinicName || "";
  if (field === "parent1Name") return patient.parent1?.name || "";
  if (field === "parent1Phone") return patient.parent1?.phone || "";
  if (field === "parent1Id") return patient.parent1?.id || "";
  if (field === "parent2Name") return patient.parent2?.name || "";
  if (field === "parent2Phone") return patient.parent2?.phone || "";
  if (field === "parent2Id") return patient.parent2?.id || "";
  if (field === "emergencyContactName") return patient.emergencyContact?.name || "";
  if (field === "emergencyContactRelation") return patient.emergencyContact?.relation || "";
  if (field === "emergencyContactPhone") return patient.emergencyContact?.phone || "";
  return patient[field] || "";
};

// Helper function to validate patient has all required placeholder fields
const validatePatientFieldsForForm = (htmlContent, patient, extraData = {}) => {
  const requiredFields = extractPatientFieldPlaceholders(htmlContent);
  const missingFields = [];

  requiredFields.forEach(({ field, placeholder }) => {
    const value = resolveFieldValue(field, patient, extraData);
    if (!value || (typeof value === "string" && value.trim() === "")) {
      missingFields.push({ field, placeholder });
    }
  });

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
};


// Template Field Builder Component
function TemplateFieldBuilder({ fields, onChange }) {
  const addField = () => {
    const newField = { id: generateFieldId(), label: "", type: "short_text", required: false, order: fields.length };
    onChange([...fields, newField]);
  };

  const updateField = (fieldId, updates) => {
    onChange(fields.map((field) => (field.id === fieldId ? { ...field, ...updates } : field)));
  };

  const changeFieldType = (fieldId, newType) => {
    const updates = { type: newType };
    if (isChoiceFieldType(newType)) {
      const current = fields.find((f) => f.id === fieldId);
      if (!current?.options?.length) updates.options = ["", ""];
    }
    if (newType === "section_header") updates.required = false;
    updateField(fieldId, updates);
  };

  const updateOption = (fieldId, optionIndex, value) => {
    const field = fields.find((f) => f.id === fieldId);
    const options = [...(field?.options || [])];
    options[optionIndex] = value;
    updateField(fieldId, { options });
  };

  const addOption = (fieldId) => {
    const field = fields.find((f) => f.id === fieldId);
    updateField(fieldId, { options: [...(field?.options || []), ""] });
  };

  const removeOption = (fieldId, optionIndex) => {
    const field = fields.find((f) => f.id === fieldId);
    updateField(fieldId, { options: (field?.options || []).filter((_, idx) => idx !== optionIndex) });
  };

  const removeField = (fieldId) => {
    onChange(fields.filter((field) => field.id !== fieldId));
  };

  // Immutable reorder: fields may come from a frozen RTK Query cache, so never mutate in place
  const reorderFields = (fromIndex, toIndex) => {
    const sorted = [...fields].sort((a, b) => a.order - b.order);
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= sorted.length || toIndex >= sorted.length) return;
    const [moved] = sorted.splice(fromIndex, 1);
    sorted.splice(toIndex, 0, moved);
    onChange(sorted.map((field, idx) => ({ ...field, order: idx })));
  };

  const moveField = (fieldId, direction) => {
    const sorted = [...fields].sort((a, b) => a.order - b.order);
    const index = sorted.findIndex((f) => f.id === fieldId);
    if (index === -1) return;
    reorderFields(index, direction === "up" ? index - 1 : index + 1);
  };

  const { listRef, dragIndex, overIndex, getHandleProps } = useHandleReorder(reorderFields);

  return (
    <div className="template-field-builder">
      <div className="fields-list" ref={listRef}>
        {fields.length === 0 ? (
          <div className="no-fields">
            <GridFour size={32} weight="duotone" />
            <p>אין שדות עדיין. הוסף שדה חדש כדי להתחיל.</p>
          </div>
        ) : (
          [...fields].sort((a, b) => a.order - b.order).map((field, index) => (
            <div key={field.id} className={`field-item${dragIndex === index ? " is-dragging" : ""}${overIndex === index && dragIndex !== null && dragIndex !== index ? " is-drop-target" : ""}`} data-reorder-index={index}>
              <div className="field-drag-handle" {...getHandleProps(index)}><DotsSixVertical size={20} weight="bold" /></div>
              <div className="field-content">
                <div className="field-row">
                  <div className="field-input-group">
                    <label>{field.type === "section_header" ? "טקסט הכותרת" : "שם השדה"}</label>
                    <input type="text" value={field.label} onChange={(e) => updateField(field.id, { label: e.target.value })} placeholder={field.type === "section_header" ? "הכנס כותרת" : "הכנס שם לשדה"} />
                  </div>
                  <div className="field-input-group field-type-select">
                    <label>סוג שדה</label>
                    <select value={field.type} onChange={(e) => changeFieldType(field.id, e.target.value)}>
                      {FIELD_TYPES.map((type) => (<option key={type.value} value={type.value}>{type.label}</option>))}
                    </select>
                  </div>
                </div>
                {isChoiceFieldType(field.type) && (
                  <div className="field-choice-options">
                    <label className="choice-options-label">אפשרויות לבחירה</label>
                    {(field.options || []).map((option, optIdx) => (
                      <div key={optIdx} className="choice-option-row">
                        <input type="text" value={option} onChange={(e) => updateOption(field.id, optIdx, e.target.value)} placeholder={`אפשרות ${optIdx + 1}`} dir="rtl" />
                        <button type="button" className="field-action-btn delete" onClick={() => removeOption(field.id, optIdx)} disabled={(field.options || []).length <= 2} title="הסר אפשרות">
                          <X size={14} weight="bold" />
                        </button>
                      </div>
                    ))}
                    <button type="button" className="add-choice-option-btn" onClick={() => addOption(field.id)}>
                      <Plus size={14} weight="bold" />
                      <span>הוסף אפשרות</span>
                    </button>
                  </div>
                )}
                {field.type !== "checkbox" && field.type !== "section_header" && (
                  <div className="field-options">
                    <label className="required-checkbox-label">
                      <input type="checkbox" checked={field.required} onChange={(e) => updateField(field.id, { required: e.target.checked })} />
                      <span className="checkbox-text">שדה חובה</span>
                    </label>
                  </div>
                )}
              </div>
              <div className="field-actions">
                <button type="button" className="field-action-btn" onClick={() => moveField(field.id, "up")} disabled={index === 0} title="הזז למעלה"><CaretUp size={16} weight="bold" /></button>
                <button type="button" className="field-action-btn" onClick={() => moveField(field.id, "down")} disabled={index === fields.length - 1} title="הזז למטה"><CaretDown size={16} weight="bold" /></button>
                <button type="button" className="field-action-btn delete" onClick={() => removeField(field.id)} title="מחק שדה"><Trash size={16} weight="regular" /></button>
              </div>
            </div>
          ))
        )}
      </div>
      <button type="button" className="add-field-btn" onClick={addField}><Plus size={18} weight="bold" /><span>הוסף שדה</span></button>
    </div>
  );
}

// Template Preview Component
function TemplatePreview({ template, onClose }) {
  return (
    <div className="template-preview-overlay" onClick={onClose}>
      <div className="template-preview-container" onClick={(e) => e.stopPropagation()}>
        <div className="preview-header">
          <h3><Eye size={20} weight="regular" />תצוגה מקדימה - {template.name}</h3>
          <ModalCloseButton onClick={onClose} />
        </div>
        <div className="preview-content">
          <div className="preview-form">
            <h2 className="preview-title">{template.name}</h2>
            {template.fields?.length === 0 ? (
              <p className="preview-empty">אין שדות בתבנית זו</p>
            ) : (
              template.fields?.map((field) => {
                if (field.type === "section_header") {
                  return (<div key={field.id} className="qp-section-header">{field.label || "כותרת"}</div>);
                }
                return (
                <div key={field.id} className="preview-field">
                  <label className={field.required ? "required" : ""}>{field.label}{field.required && <span className="required-mark">*</span>}</label>
                  {field.type === "short_text" && <input type="text" disabled placeholder="טקסט קצר..." />}
                  {field.type === "long_text" && <textarea disabled placeholder="טקסט ארוך..." className="long-textarea" />}
                  {field.type === "date" && <DatePicker disabled />}
                  {field.type === "checkbox" && (<label className="preview-checkbox"><input type="checkbox" disabled /><span>{field.label}</span></label>)}
                  {field.type === "select" && (
                    <select disabled dir="rtl">
                      <option>בחר תשובה...</option>
                      {(field.options || []).filter((opt) => opt.trim()).map((option, optIdx) => (<option key={optIdx}>{option}</option>))}
                    </select>
                  )}
                  {field.type === "radio" && (
                    <div className="qp-radio-options">
                      {(field.options || []).filter((opt) => opt.trim()).map((option, optIdx) => (
                        <label key={optIdx} className="qp-radio"><input type="radio" disabled name={`preview-${field.id}`} /><span>{option}</span></label>
                      ))}
                    </div>
                  )}
                  {field.type === "multi_select" && (
                    <div className="qp-radio-options">
                      {(field.options || []).filter((opt) => opt.trim()).map((option, optIdx) => (
                        <label key={optIdx} className="qp-radio"><input type="checkbox" disabled /><span>{option}</span></label>
                      ))}
                    </div>
                  )}
                </div>
                );
              })
            )}
          </div>
        </div>
        <div className="preview-footer">
          <button className="btn-secondary" onClick={onClose}><ArrowLeft size={18} weight="bold" />חזור לעריכה</button>
        </div>
      </div>
    </div>
  );
}

// Questionnaire Field Builder Component
function QuestionnaireFieldBuilder({ fields, onChange }) {
  const addField = () => {
    const newField = { id: generateFieldId(), label: "", type: "short_text", required: false, order: fields.length };
    onChange([...fields, newField]);
  };

  const updateField = (fieldId, updates) => {
    onChange(fields.map((field) => (field.id === fieldId ? { ...field, ...updates } : field)));
  };

  const changeFieldType = (fieldId, newType) => {
    const updates = { type: newType };
    if (isChoiceFieldType(newType)) {
      const current = fields.find((f) => f.id === fieldId);
      if (!current?.options?.length) updates.options = ["", ""];
    }
    if (newType === "section_header") updates.required = false;
    updateField(fieldId, updates);
  };

  const updateOption = (fieldId, optionIndex, value) => {
    const field = fields.find((f) => f.id === fieldId);
    const options = [...(field?.options || [])];
    options[optionIndex] = value;
    updateField(fieldId, { options });
  };

  const addOption = (fieldId) => {
    const field = fields.find((f) => f.id === fieldId);
    updateField(fieldId, { options: [...(field?.options || []), ""] });
  };

  const removeOption = (fieldId, optionIndex) => {
    const field = fields.find((f) => f.id === fieldId);
    updateField(fieldId, { options: (field?.options || []).filter((_, idx) => idx !== optionIndex) });
  };

  const removeField = (fieldId) => {
    onChange(fields.filter((field) => field.id !== fieldId));
  };

  // Immutable reorder: fields may come from a frozen RTK Query cache, so never mutate in place
  const reorderFields = (fromIndex, toIndex) => {
    const sorted = [...fields].sort((a, b) => a.order - b.order);
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= sorted.length || toIndex >= sorted.length) return;
    const [moved] = sorted.splice(fromIndex, 1);
    sorted.splice(toIndex, 0, moved);
    onChange(sorted.map((field, idx) => ({ ...field, order: idx })));
  };

  const moveField = (fieldId, direction) => {
    const sorted = [...fields].sort((a, b) => a.order - b.order);
    const index = sorted.findIndex((f) => f.id === fieldId);
    if (index === -1) return;
    reorderFields(index, direction === "up" ? index - 1 : index + 1);
  };

  const { listRef, dragIndex, overIndex, getHandleProps } = useHandleReorder(reorderFields);

  return (
    <div className="template-field-builder">
      <div className="fields-list" ref={listRef}>
        {fields.length === 0 ? (
          <div className="no-fields">
            <ListBullets size={32} weight="duotone" />
            <p>אין שאלות עדיין. הוסף שאלה חדשה כדי להתחיל.</p>
          </div>
        ) : (
          [...fields].sort((a, b) => a.order - b.order).map((field, index) => (
            <div key={field.id} className={`field-item${dragIndex === index ? " is-dragging" : ""}${overIndex === index && dragIndex !== null && dragIndex !== index ? " is-drop-target" : ""}`} data-reorder-index={index}>
              <div className="field-drag-handle" {...getHandleProps(index)}><DotsSixVertical size={20} weight="bold" /></div>
              <div className="field-content">
                <div className="field-row">
                  <div className="field-input-group">
                    <label>{field.type === "section_header" ? "טקסט הכותרת" : "שם השאלה"}</label>
                    <input type="text" value={field.label} onChange={(e) => updateField(field.id, { label: e.target.value })} placeholder={field.type === "section_header" ? "הכנס כותרת" : "הכנס שאלה"} dir="rtl" />
                  </div>
                  <div className="field-input-group field-type-select">
                    <label>סוג תשובה</label>
                    <select value={field.type} onChange={(e) => changeFieldType(field.id, e.target.value)}>
                      {QUESTIONNAIRE_FIELD_TYPES.map((type) => (<option key={type.value} value={type.value}>{type.label}</option>))}
                    </select>
                  </div>
                </div>
                {isChoiceFieldType(field.type) && (
                  <div className="field-choice-options">
                    <label className="choice-options-label">אפשרויות לבחירה</label>
                    {(field.options || []).map((option, optIdx) => (
                      <div key={optIdx} className="choice-option-row">
                        <input type="text" value={option} onChange={(e) => updateOption(field.id, optIdx, e.target.value)} placeholder={`אפשרות ${optIdx + 1}`} dir="rtl" />
                        <button type="button" className="field-action-btn delete" onClick={() => removeOption(field.id, optIdx)} disabled={(field.options || []).length <= 2} title="הסר אפשרות">
                          <X size={14} weight="bold" />
                        </button>
                      </div>
                    ))}
                    <button type="button" className="add-choice-option-btn" onClick={() => addOption(field.id)}>
                      <Plus size={14} weight="bold" />
                      <span>הוסף אפשרות</span>
                    </button>
                  </div>
                )}
                {field.type !== "checkbox" && field.type !== "section_header" && (
                  <div className="field-options">
                    <label className="required-checkbox-label">
                      <input type="checkbox" checked={field.required} onChange={(e) => updateField(field.id, { required: e.target.checked })} />
                      <span className="checkbox-text">שאלת חובה</span>
                    </label>
                  </div>
                )}
              </div>
              <div className="field-actions">
                <button type="button" className="field-action-btn" onClick={() => moveField(field.id, "up")} disabled={index === 0} title="הזז למעלה"><CaretUp size={16} weight="bold" /></button>
                <button type="button" className="field-action-btn" onClick={() => moveField(field.id, "down")} disabled={index === fields.length - 1} title="הזז למטה"><CaretDown size={16} weight="bold" /></button>
                <button type="button" className="field-action-btn delete" onClick={() => removeField(field.id)} title="מחק שאלה"><Trash size={16} weight="regular" /></button>
              </div>
            </div>
          ))
        )}
      </div>
      <button type="button" className="add-field-btn" onClick={addField}><Plus size={18} weight="bold" /><span>הוסף שאלה</span></button>
    </div>
  );
}

// Read-only preview of questionnaire fields, as the patient will see them
function QuestionnairePreviewFields({ fields, requireSignature = false }) {
  const sortedFields = [...(fields || [])].sort((a, b) => a.order - b.order);

  return (
    <div className="questionnaire-preview">
      {requireSignature && (
        <div className="qsig-preview-banner">
          <Signature size={16} weight="fill" />
          <span>שאלון זה דורש חתימה דיגיטלית לפני השליחה</span>
        </div>
      )}
      {sortedFields.map((field) => {
        if (field.type === "section_header") {
          return <div key={field.id} className="qp-section-header">{field.label || "כותרת"}</div>;
        }

        return (
          <div key={field.id} className="qp-field">
            {field.type !== "checkbox" && (
              <label className="qp-label">
                {field.label || "שאלה ללא שם"}
                {field.required && <span className="qp-required">*</span>}
              </label>
            )}
            {field.type === "short_text" && <input type="text" disabled placeholder="טקסט קצר..." />}
            {field.type === "long_text" && <textarea disabled placeholder="טקסט ארוך..." rows={3} />}
            {field.type === "date" && <DatePicker disabled />}
            {field.type === "checkbox" && (
              <label className="qp-checkbox"><input type="checkbox" disabled /><span>{field.label || "שאלה ללא שם"}</span></label>
            )}
            {field.type === "select" && (
              <select disabled dir="rtl">
                <option>בחר תשובה...</option>
                {(field.options || []).filter((opt) => opt.trim()).map((option, optIdx) => (<option key={optIdx}>{option}</option>))}
              </select>
            )}
            {field.type === "radio" && (
              <div className="qp-radio-options">
                {(field.options || []).filter((opt) => opt.trim()).map((option, optIdx) => (
                  <label key={optIdx} className="qp-radio">
                    <input type="radio" disabled name={`preview-${field.id}`} />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            )}
            {field.type === "multi_select" && (
              <div className="qp-radio-options">
                {(field.options || []).filter((opt) => opt.trim()).map((option, optIdx) => (
                  <label key={optIdx} className="qp-radio">
                    <input type="checkbox" disabled />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {requireSignature && (
        <div className="qp-field qsig-preview-block" aria-hidden="true">
          <label className="qp-label">
            חתימת המטופל/ת<span className="qp-required">*</span>
          </label>
          <div className="qsig-preview-pad">
            <PencilLine size={22} weight="regular" />
            <span>כאן המטופל/ת יחתמו עם האצבע או העכבר</span>
          </div>
        </div>
      )}
    </div>
  );
}

function TasksRemindersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const therapistProfile = useSelector(selectTherapistProfile);
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const isAdmin = useSelector(selectIsAdmin);
  // Permissions v2 (legacy therapists default to false → today's UI)
  const canViewAllPatients = useSelector(selectHasPermission("patients.viewAllClinic"));
  const canViewAllClinicTasks = useSelector(selectHasPermission("tasks.viewAllClinic"));
  // Leads v2: clinic-level toggle (admins always). The tab stays visible; LeadsTab shows a message when off.
  const canManageLeads = useSelector(selectHasPermission("leads.access"));
  const canViewAllLeads = isAdmin || canManageLeads;
  // Clinic-wide patient scope: admin, or therapist granted patients.viewAllClinic
  const canSeeAllPatients = isAdmin || canViewAllPatients;
  const clinicProfile = useSelector(selectClinicProfile);
  const { showSuccess, showError, showWarning } = useToast();

  // Main tabs: tasks, reminders, leads, forms, questionnaires, templates
  const [activeMainTab, setActiveMainTab] = useState("tasks");
  const [searchQuery, setSearchQuery] = useState("");
  // Status filter for tasks
  const [statusFilter, setStatusFilter] = useState("all");
  // Leads v2: deep-link hand-off to <LeadsTab /> ({ action: "new" } | { leadId })
  const [leadsDeepLink, setLeadsDeepLink] = useState(null);

  // Modal states
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingReminder, setEditingReminder] = useState(null);
  const [historyData, setHistoryData] = useState(null);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    type: "danger",
    icon: "delete",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // Form states
  const [taskForm, setTaskForm] = useState({
    title: "",
    note: "",
    status: "todo",
    dueDate: "",
    dueTime: "",
    categoryId: "",
  });
  const [reminderForm, setReminderForm] = useState({
    title: "",
    content: "",
    categoryId: "",
  });
  // =====================================================
  // FORMS PAGE STATE
  // =====================================================
  
  // Forms modal states
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showSendFormModal, setShowSendFormModal] = useState(false);
  // Form id handed off from the editor page (?sendForm=<id>) — opened once forms load
  const [pendingSendFormId, setPendingSendFormId] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [sendingForm, setSendingForm] = useState(null);

  // Questionnaire modal states
  const [showQuestionnaireModal, setShowQuestionnaireModal] = useState(false);
  const [questionnairePreviewMode, setQuestionnairePreviewMode] = useState(false);
  const [editingQuestionnaire, setEditingQuestionnaire] = useState(null);
  const [showSendQuestionnaireModal, setShowSendQuestionnaireModal] = useState(false);
  const [sendingQuestionnaire, setSendingQuestionnaire] = useState(null);
  const [selectedQuestionnairePatients, setSelectedQuestionnairePatients] = useState([]);
  const [questionnairePatientSearch, setQuestionnairePatientSearch] = useState("");

  // Toggle for admin to show all clinic patients vs only their own
  const [showAllClinicPatients, setShowAllClinicPatients] = useState(false);
  // Clinic-wide patient toggle: admins (or patients.viewAllClinic) of multi-therapist clinics
  const canToggleClinicPatients = canSeeAllPatients && clinicProfile?.isSoloClinic !== true;

  // Send form modal state
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [patientSearchQuery, setPatientSearchQuery] = useState("");
  const [smsTemplate, setSmsTemplate] = useState(clinicProfile?.signatureSmsTemplate || DEFAULT_SIGNATURE_SMS_TEMPLATE);
  const [missingPatientFields, setMissingPatientFields] = useState([]);

  // Forms form states
  const [templateData, setTemplateData] = useState({ name: "", fields: [] });
  // requireSignature: patient must sign (draw / type) before submitting.
  // Legacy questionnaires have no flag → false.
  const [questionnaireData, setQuestionnaireData] = useState({ name: "", fields: [], requireSignature: false });

  // Handle URL query parameters for deep linking (e.g., ?tab=leads&action=new)
  useEffect(() => {
    const tab = searchParams.get("tab");
    const action = searchParams.get("action");

    if (tab === "leads") {
      setActiveMainTab("leads");
      const leadId = searchParams.get("lead");
      if (action === "new") setLeadsDeepLink({ action: "new" });
      else if (leadId) setLeadsDeepLink({ leadId });
      setSearchParams({}, { replace: true });
    } else if (tab === "reminders") {
      setActiveMainTab("reminders");
      setSearchParams({}, { replace: true });
    } else if (tab === "tasks") {
      setActiveMainTab("tasks");
      if (action === "new") {
        const prefillDate = searchParams.get("date");
        setEditingTask(null);
        setTaskForm({
          title: "",
          note: "",
          status: "todo",
          dueDate: prefillDate || "",
          dueTime: "",
          categoryId: "",
        });
        setShowTaskModal(true);
      }
      setSearchParams({}, { replace: true });
    } else if (tab === "forms" || tab === "pendingSignatures") {
      setActiveMainTab("forms");
      // `sendForm` is consumed by a dedicated effect once forms are loaded
      // (hand-off from the form editor's "send for signature" button).
      const sendFormId = searchParams.get("sendForm");
      if (sendFormId) {
        setPendingSendFormId(sendFormId);
      }
      setSearchParams({}, { replace: true });
    } else if (tab === "questionnaires" || tab === "pendingQuestionnaires") {
      setActiveMainTab("questionnaires");
      setSearchParams({}, { replace: true });
    } else if (tab === "templates") {
      setActiveMainTab("templates");
      setSearchParams({}, { replace: true });
    } else if (tab === "myFolder") {
      setActiveMainTab("myFolder");
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Queries - Personal tasks/reminders with real-time updates
  const { data: personalTasks = [], isLoading: isLoadingPersonalTasks, refetch: refetchPersonalTasks } =
    useGetTasksQuery(
      { clinicId, scope: "personal", therapistId },
      { skip: !clinicId || !therapistId, pollingInterval: DEFAULT_POLLING_INTERVAL }
    );

  const {
    data: personalReminders = [],
    isLoading: isLoadingPersonalReminders,
    refetch: refetchPersonalReminders,
  } = useGetRemindersQuery(
    { clinicId, scope: "personal", therapistId },
    { skip: !clinicId || !therapistId, pollingInterval: DEFAULT_POLLING_INTERVAL }
  );

  // Queries - Clinic tasks/reminders (to filter assigned to me) with real-time updates
  const { data: clinicTasks = [], refetch: refetchClinicTasks } = useGetTasksQuery(
    { clinicId, scope: "clinic" },
    { skip: !clinicId, pollingInterval: DEFAULT_POLLING_INTERVAL }
  );

  const { data: clinicReminders = [], refetch: refetchClinicReminders } = useGetRemindersQuery(
    { clinicId, scope: "clinic" },
    { skip: !clinicId, pollingInterval: DEFAULT_POLLING_INTERVAL }
  );

  const { data: taskCategories = [] } = useGetCategoriesQuery(
    { clinicId, type: "task" },
    { skip: !clinicId }
  );

  const { data: reminderCategories = [] } = useGetCategoriesQuery(
    { clinicId, type: "reminder" },
    { skip: !clinicId }
  );

  // =====================================================
  // FORMS QUERIES
  // =====================================================
  
  const { data: cloudForms = [], isLoading: isLoadingForms } = useGetFormsQuery(
    { clinicId },
    { skip: !clinicId }
  );
  
  const { data: cloudTemplates = [], isLoading: isLoadingTemplates } = useGetTemplatesQuery(
    { clinicId },
    { skip: !clinicId }
  );

  const { data: cloudQuestionnaires = [], isLoading: isLoadingQuestionnaires, refetch: refetchQuestionnaires } = useGetQuestionnairesQuery(
    { clinicId },
    { skip: !clinicId }
  );

  // System (built-in) content filtered by clinic type
  const clinicType = clinicProfile?.type;
  const systemForms = useMemo(() => getSystemForms(clinicType || ""), [clinicType]);
  const systemTemplates = useMemo(() => getSystemTemplates(clinicType || ""), [clinicType]);
  const systemQuestionnaires = useMemo(() => getSystemQuestionnaires(clinicType || ""), [clinicType]);

  // Merged lists: system items first, then cloud items
  const forms = useMemo(() => [...systemForms, ...cloudForms], [systemForms, cloudForms]);
  const templates = useMemo(() => [...systemTemplates, ...cloudTemplates], [systemTemplates, cloudTemplates]);
  const questionnaires = useMemo(() => [...systemQuestionnaires, ...cloudQuestionnaires], [systemQuestionnaires, cloudQuestionnaires]);

  const { data: pendingQuestionnaireLinks = [], isLoading: isLoadingPendingQuestionnaireLinks } = useGetPendingQuestionnaireLinksQuery(
    { clinicId, therapistId: canSeeAllPatients ? null : therapistId },
    { skip: !clinicId }
  );

  // Patients query - my patients (always fetch)
  const { data: myPatientsData, isLoading: isLoadingMyPatients } = useGetPatientsQuery(
    { clinicId, therapistId, pageSize: PATIENTS_PICKER_PAGE_SIZE },
    { skip: !clinicId || !therapistId }
  );
  const myPatients = useMemo(() => myPatientsData?.patients || [], [myPatientsData?.patients]);

  // All clinic patients query (only for admin, only when toggle is on)
  const { data: allPatientsData, isLoading: isLoadingAllPatients } = useGetPatientsQuery(
    { clinicId, therapistId: null, pageSize: PATIENTS_PICKER_PAGE_SIZE },
    { skip: !clinicId || !canSeeAllPatients || !showAllClinicPatients }
  );
  const allClinicPatients = useMemo(() => allPatientsData?.patients || [], [allPatientsData?.patients]);

  // Select the correct patient list based on toggle
  const patients = useMemo(() => {
    if (canSeeAllPatients && showAllClinicPatients) {
      return allClinicPatients;
    }
    return myPatients;
  }, [canSeeAllPatients, showAllClinicPatients, allClinicPatients, myPatients]);

  const isLoadingPatients = canSeeAllPatients && showAllClinicPatients ? isLoadingAllPatients : isLoadingMyPatients;

  const clinicExtraData = useMemo(() => ({
    clinicName: clinicProfile?.name || "",
  }), [clinicProfile?.name]);

  // Validate patient fields when patient is selected for form signing
  useEffect(() => {
    if (sendingForm && selectedPatientId) {
      const selectedPatient = patients.find((p) => p.id === selectedPatientId);
      if (selectedPatient) {
        const validation = validatePatientFieldsForForm(sendingForm.content, selectedPatient, clinicExtraData);
        setMissingPatientFields(validation.missingFields);
      }
    } else {
      setMissingPatientFields([]);
    }
  }, [sendingForm, selectedPatientId, patients, clinicExtraData]);

  const { data: pendingLinks = [], isLoading: isLoadingPendingLinks } = useGetPendingSignatureLinksQuery(
    { clinicId, therapistId: canSeeAllPatients ? null : therapistId },
    { skip: !clinicId }
  );

  // Combine refetch functions for visibility refresh
  const combinedRefetch = useCombinedRefetch([
    refetchPersonalTasks,
    refetchPersonalReminders,
    refetchClinicTasks,
    refetchClinicReminders,
  ]);

  // Auto-refresh when tab becomes visible
  useVisibilityRefresh(combinedRefetch);

  // Mutations
  const [createTask, { isLoading: isCreatingTask }] = useCreateTaskMutation();
  const [updateTask, { isLoading: isUpdatingTask }] = useUpdateTaskMutation();
  const [reorderTasks] = useReorderTasksMutation();
  const [deleteTask] = useDeleteTaskMutation();
  const [createReminder, { isLoading: isCreatingReminder }] =
    useCreateReminderMutation();
  const [updateReminder, { isLoading: isUpdatingReminder }] =
    useUpdateReminderMutation();
  const [deleteReminder] = useDeleteReminderMutation();

  // =====================================================
  // FORMS MUTATIONS
  // =====================================================
  
  const [createForm] = useCreateFormMutation();
  const [deleteForm] = useDeleteFormMutation();
  const [duplicateForm] = useDuplicateFormMutation();
  const [createTemplate, { isLoading: isCreatingTemplate }] = useCreateTemplateMutation();
  const [updateTemplate, { isLoading: isUpdatingTemplate }] = useUpdateTemplateMutation();
  const [deleteTemplate] = useDeleteTemplateMutation();
  const [duplicateTemplate] = useDuplicateTemplateMutation();
  const [sendFormForSignature, { isLoading: isSendingForm }] = useSendFormForSignatureMutation();
  const [cancelSignatureLink] = useCancelSignatureLinkMutation();
  const [deleteSignatureLink] = useDeleteSignatureLinkMutation();
  const [createQuestionnaire, { isLoading: isCreatingQuestionnaire }] = useCreateQuestionnaireMutation();
  const [updateQuestionnaire, { isLoading: isUpdatingQuestionnaire }] = useUpdateQuestionnaireMutation();
  const [deleteQuestionnaire] = useDeleteQuestionnaireMutation();
  const [duplicateQuestionnaire] = useDuplicateQuestionnaireMutation();
  const [sendQuestionnaireToPatients, { isLoading: isSendingQuestionnaire }] = useSendQuestionnaireToPatientsMutation();

  // Clinic tasks assigned to this therapist. Non-admins granted tasks.viewAllClinic
  // see every clinic-scope task here; admins keep today's view (full board lives
  // on AdminTasksPage).
  const myClinicTasks = useMemo(() => {
    if (!isAdmin && canViewAllClinicTasks) return clinicTasks;
    return clinicTasks.filter((task) => task.assignedTo === therapistId);
  }, [clinicTasks, therapistId, isAdmin, canViewAllClinicTasks]);

  // Clinic reminders created by this therapist
  const myClinicReminders = useMemo(() => {
    return clinicReminders.filter(
      (reminder) => reminder.createdBy === therapistId
    );
  }, [clinicReminders, therapistId]);

  // In-app expiration toast for time-precise tasks: schedule one timer per
  // upcoming deadline (within 24h). The Cloud Function still fires the push +
  // in-app notification record; this just gives the open tab an immediate alert
  // and a refetch so the card visibly flips to "past-due".
  const expirationToastSeenRef = useRef(new Set());
  useEffect(() => {
    const candidates = [...personalTasks, ...myClinicTasks].filter(
      (t) =>
        t.hasDueTime &&
        t.dueDate &&
        t.status !== "done" &&
        !expirationToastSeenRef.current.has(t.id)
    );

    const now = Date.now();
    const WINDOW_MS = 24 * 60 * 60 * 1000;
    const timers = [];

    for (const task of candidates) {
      const fireAt = new Date(task.dueDate).getTime();
      const delay = fireAt - now;
      if (delay <= 0 || delay > WINDOW_MS) continue;

      const timerId = setTimeout(() => {
        if (expirationToastSeenRef.current.has(task.id)) return;
        expirationToastSeenRef.current.add(task.id);
        showWarning(`המשימה "${task.title}" פגה`);
        refetchPersonalTasks?.();
        refetchClinicTasks?.();
      }, delay);
      timers.push(timerId);
    }

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [
    personalTasks,
    myClinicTasks,
    showWarning,
    refetchPersonalTasks,
    refetchClinicTasks,
  ]);

  // Sort tasks by the user's manual order (drag-and-drop). Tasks that were never
  // reordered have no `order` field and float to the top, newest first — so newly
  // created tasks stay visible at the top until the user arranges them.
  const getTaskSortValue = (task) => {
    if (typeof task.order === "number") return task.order;
    const ms = task.createdAt ? new Date(task.createdAt).getTime() : 0;
    return -ms;
  };
  const sortTasksByOrder = (tasks) => {
    return [...tasks].sort((a, b) => getTaskSortValue(a) - getTaskSortValue(b));
  };

  // Filter and sort personal tasks by manual order
  const filteredPersonalTasks = useMemo(() => {
    let tasks = personalTasks;
    if (statusFilter !== "all") {
      tasks = tasks.filter((task) => task.status === statusFilter);
    }
    return sortTasksByOrder(tasks);
  }, [personalTasks, statusFilter]);

  // Filter and sort my clinic tasks by manual order
  const filteredMyClinicTasks = useMemo(() => {
    let tasks = myClinicTasks;
    if (statusFilter !== "all") {
      tasks = tasks.filter((task) => task.status === statusFilter);
    }
    return sortTasksByOrder(tasks);
  }, [myClinicTasks, statusFilter]);

  // ---- Drag-and-drop manual reorder (personal tasks, mobile + desktop) ----
  // Enabled only in the "all" view so we never renumber a filtered subset and
  // corrupt the order of hidden tasks.
  const canReorderTasks = statusFilter === "all";
  const [dragTaskId, setDragTaskId] = useState(null);
  const [dragOrderIds, setDragOrderIds] = useState(null); // live id order while dragging
  const dragRef = useRef({ ids: [], activeId: null, moved: false, grabY: 0, pointerY: 0 });
  const heldSlotTopRef = useRef(0); // current layout top of the held card's slot
  const lastTaskDragEndRef = useRef(0); // timestamp of last real drag; swallows the trailing click

  // The list to render: the live drag order while dragging, else the sorted list.
  const displayedPersonalTasks = useMemo(() => {
    if (!dragOrderIds) return filteredPersonalTasks;
    const byId = new Map(filteredPersonalTasks.map((t) => [t.id, t]));
    return dragOrderIds.map((id) => byId.get(id)).filter(Boolean);
  }, [dragOrderIds, filteredPersonalTasks]);

  // FLIP animation: when the order changes, slide each card from its previous
  // position to its new one so reordering feels smooth (desktop-grade) instead of
  // snapping. Cards register their DOM node here; the layout effect measures
  // before/after and plays an inverted transform.
  const taskCardRefs = useRef(new Map());
  const prevTaskRectsRef = useRef(new Map());
  const setTaskCardRef = (id, el) => {
    if (el) taskCardRefs.current.set(id, el);
    else taskCardRefs.current.delete(id);
  };

  useLayoutEffect(() => {
    const els = taskCardRefs.current;
    const prev = prevTaskRectsRef.current;
    const nextRects = new Map();
    els.forEach((el, id) => {
      const rect = el.getBoundingClientRect();
      nextRects.set(id, rect);
      // The card under the finger stays "held" — it follows the pointer
      // (offset from its current slot), so only the surrounding cards slide.
      if (id === dragTaskId) {
        const { grabY, pointerY } = dragRef.current;
        // rect.top includes any current transform; slot top is the untransformed
        // layout position.
        const slotTop = rect.top - getTranslateY(el);
        heldSlotTopRef.current = slotTop;
        el.style.transition = "none";
        el.style.transform = `translateY(${pointerY - slotTop - grabY}px)`;
        return;
      }
      const old = prev.get(id);
      if (!old) return;
      const dy = old.top - rect.top;
      const dx = old.left - rect.left;
      if (!dx && !dy) return;
      // FLIP: invert instantly, flush styles so the inverted frame is committed,
      // then release to animate to the new slot. (Without the forced reflow the
      // set + clear collapse into one style pass and nothing animates.)
      if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
      el.style.transition = "none";
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      void el.offsetHeight;
      el.style.transition = "transform 220ms cubic-bezier(0.2, 0, 0, 1)";
      el.style.transform = "";
    });
    prevTaskRectsRef.current = nextRects;
  }, [displayedPersonalTasks, dragTaskId]);

  const handleTaskDragMove = (e) => {
    const { activeId, ids, grabY } = dragRef.current;
    if (!activeId) return;
    dragRef.current.pointerY = e.clientY;

    // Held card follows the finger/cursor between reorders.
    const heldEl = taskCardRefs.current.get(activeId);
    if (heldEl) {
      heldEl.style.transition = "none";
      heldEl.style.transform = `translateY(${e.clientY - heldSlotTopRef.current - grabY}px)`;
    }

    const el = document.elementFromPoint(e.clientX, e.clientY);
    const card = el?.closest?.("[data-task-id]");
    if (!card) return;
    const overId = card.getAttribute("data-task-id");
    if (!overId || overId === activeId) return;

    // Insert relative to the target's vertical midpoint so the position is stable
    // (idempotent while hovering) — no oscillation between two neighbours.
    const rect = card.getBoundingClientRect();
    const insertAfter = e.clientY > rect.top + rect.height / 2;
    const without = ids.filter((id) => id !== activeId);
    const overIdx = without.indexOf(overId);
    if (overIdx === -1) return;
    const insertAt = insertAfter ? overIdx + 1 : overIdx;
    const next = [...without];
    next.splice(insertAt, 0, activeId);

    // Skip if the order is unchanged (avoids needless re-renders / FLIP churn).
    if (next.length === ids.length && next.every((id, i) => id === ids[i])) return;

    dragRef.current = { ...dragRef.current, ids: next, moved: true };
    setDragOrderIds(next);
  };

  const handleTaskDragEnd = async () => {
    window.removeEventListener("pointermove", handleTaskDragMove);
    window.removeEventListener("pointerup", handleTaskDragEnd);
    const { ids, moved, activeId } = dragRef.current;
    dragRef.current = { ids: [], activeId: null, moved: false, grabY: 0, pointerY: 0 };
    // Settle: animate the held card from under the finger into its slot.
    const heldEl = activeId && taskCardRefs.current.get(activeId);
    if (heldEl) {
      heldEl.style.transition = "transform 220ms cubic-bezier(0.2, 0, 0, 1)";
      heldEl.style.transform = "";
    }
    if (moved) lastTaskDragEndRef.current = Date.now();
    setDragTaskId(null);
    if (!moved || !clinicId) {
      setDragOrderIds(null);
      return;
    }
    // Keep the live order applied. It is cleared by the reconcile effect below
    // only once the refetched tasks report the same order — clearing earlier
    // would briefly snap the list back to the stale server order (X under Y)
    // and then re-animate, which reads as a bug.
    try {
      await reorderTasks({ clinicId, orderedTaskIds: ids }).unwrap();
    } catch (error) {
      console.error("[CK-3090] Error saving task order:", {
        errorCode: "CK-3090",
        errorMessage: error.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת סדר המשימות");
      setDragOrderIds(null); // revert to server truth on failure
    }
  };

  // Reconcile: drop the live drag order once the refetched tasks already reflect
  // it (server caught up). Orders are identical at this point, so clearing is
  // invisible — no snap, no re-animation.
  useEffect(() => {
    if (!dragOrderIds || dragTaskId) return;
    const current = filteredPersonalTasks.map((t) => t.id);
    if (
      current.length === dragOrderIds.length &&
      current.every((id, i) => id === dragOrderIds[i])
    ) {
      setDragOrderIds(null);
    }
  }, [filteredPersonalTasks, dragOrderIds, dragTaskId]);

  const handleTaskDragStart = (e, taskId) => {
    if (!canReorderTasks) return;
    e.stopPropagation();
    e.preventDefault();
    const ids = filteredPersonalTasks.map((t) => t.id);
    const cardEl = taskCardRefs.current.get(taskId);
    const cardTop = cardEl ? cardEl.getBoundingClientRect().top : e.clientY;
    heldSlotTopRef.current = cardTop;
    dragRef.current = {
      ids,
      activeId: taskId,
      moved: false,
      grabY: e.clientY - cardTop, // where inside the card it was grabbed
      pointerY: e.clientY,
    };
    setDragTaskId(taskId);
    setDragOrderIds(ids);
    try {
      e.target.setPointerCapture?.(e.pointerId);
    } catch {
      // setPointerCapture can throw if the pointer is already released; safe to ignore.
    }
    window.addEventListener("pointermove", handleTaskDragMove);
    window.addEventListener("pointerup", handleTaskDragEnd);
  };

  // Count tasks by status (personal + my clinic tasks)
  const taskCounts = useMemo(() => {
    const allTasks = [...personalTasks, ...myClinicTasks];
    const counts = { all: allTasks.length };
    TASK_STATUSES.forEach((status) => {
      counts[status.value] = allTasks.filter(
        (t) => t.status === status.value
      ).length;
    });
    return counts;
  }, [personalTasks, myClinicTasks]);

  // =====================================================
  // FORMS FILTERED MEMOS
  // =====================================================

  // Filter forms by search
  const filteredForms = useMemo(() => {
    if (!searchQuery.trim()) return forms;
    const query = searchQuery.toLowerCase();
    return forms.filter((form) => form.name?.toLowerCase().includes(query));
  }, [forms, searchQuery]);

  // Filter templates by search
  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return templates;
    const query = searchQuery.toLowerCase();
    return templates.filter((template) => template.name?.toLowerCase().includes(query));
  }, [templates, searchQuery]);

  // Filter pending links by search
  const filteredPendingLinks = useMemo(() => {
    if (!searchQuery.trim()) return pendingLinks;
    const query = searchQuery.toLowerCase();
    return pendingLinks.filter(
      (link) => link.formName?.toLowerCase().includes(query) || link.patientName?.toLowerCase().includes(query)
    );
  }, [pendingLinks, searchQuery]);

  // Filter questionnaires by search
  const filteredQuestionnaires = useMemo(() => {
    if (!searchQuery.trim()) return questionnaires;
    const query = searchQuery.toLowerCase();
    return questionnaires.filter((q) => q.name?.toLowerCase().includes(query));
  }, [questionnaires, searchQuery]);

  // Filter pending questionnaire links by search
  const filteredPendingQuestionnaireLinks = useMemo(() => {
    if (!searchQuery.trim()) return pendingQuestionnaireLinks;
    const query = searchQuery.toLowerCase();
    return pendingQuestionnaireLinks.filter(
      (link) => link.questionnaireName?.toLowerCase().includes(query) || link.patientName?.toLowerCase().includes(query)
    );
  }, [pendingQuestionnaireLinks, searchQuery]);

  // Filter patients for questionnaire send modal
  const filteredQuestionnairePatients = useMemo(() => {
    const sorted = sortPatientsByName(patients);
    if (!questionnairePatientSearch.trim()) return sorted.slice(0, 50);
    const query = questionnairePatientSearch.toLowerCase();
    return sorted.filter(
      (patient) =>
        patient.firstName?.toLowerCase().includes(query) ||
        patient.lastName?.toLowerCase().includes(query) ||
        patient.phone?.includes(query)
    ).slice(0, 50);
  }, [patients, questionnairePatientSearch]);

  // Filter patients for send form modal
  const filteredPatients = useMemo(() => {
    const sorted = sortPatientsByName(patients);
    if (!patientSearchQuery.trim()) return sorted.slice(0, 20);
    const query = patientSearchQuery.toLowerCase();
    return sorted.filter(
      (patient) =>
        patient.firstName?.toLowerCase().includes(query) ||
        patient.lastName?.toLowerCase().includes(query) ||
        patient.phone?.includes(query)
    ).slice(0, 20);
  }, [patients, patientSearchQuery]);

  // Check if user can edit/delete an item (system items are never editable)
  const canEditItem = (item) => {
    if (item.isSystem) return false;
    return isAdmin || item.createdBy === therapistId;
  };

  // Handlers
  const handleOpenTaskModal = (task = null) => {
    if (task) {
      setEditingTask(task);
      setTaskForm({
        title: task.title || "",
        note: task.note || "",
        status: task.status || "todo",
        dueDate: extractDateFromIso(task.dueDate),
        dueTime: task.hasDueTime ? extractTimeFromIso(task.dueDate) : "",
        categoryId: task.categoryId || "",
      });
    } else {
      setEditingTask(null);
      setTaskForm({
        title: "",
        note: "",
        status: "todo",
        dueDate: "",
        dueTime: "",
        categoryId: "",
      });
    }
    setShowTaskModal(true);
  };

  const handleOpenReminderModal = (reminder = null) => {
    if (reminder) {
      setEditingReminder(reminder);
      setReminderForm({
        title: reminder.title || "",
        content: reminder.content || "",
        categoryId: reminder.categoryId || "",
      });
    } else {
      setEditingReminder(null);
      setReminderForm({
        title: "",
        content: "",
        categoryId: "",
      });
    }
    setShowReminderModal(true);
  };

  const handleCloseTaskModal = () => {
    setShowTaskModal(false);
    setEditingTask(null);
    setTaskForm({
      title: "",
      note: "",
      status: "todo",
      dueDate: "",
      dueTime: "",
      categoryId: "",
    });
  };

  const handleCloseReminderModal = () => {
    setShowReminderModal(false);
    setEditingReminder(null);
    setReminderForm({
      title: "",
      content: "",
      categoryId: "",
    });
  };

  const handleSaveTask = async () => {
    if (!taskForm.title.trim()) return;

    // For editing clinic tasks, keep the scope; for new tasks, always personal
    const taskData = {
      title: taskForm.title.trim(),
      note: taskForm.note.trim() || null,
      status: taskForm.status,
      dueDate: taskForm.dueDate || null,
      dueTime: taskForm.dueDate && taskForm.dueTime ? taskForm.dueTime : null,
      categoryId: taskForm.categoryId || null,
      scope: editingTask?.scope || "personal",
    };

    try {
      if (editingTask) {
        await updateTask({
          clinicId,
          taskId: editingTask.id,
          taskData,
          editorId: therapistId,
          editorName: `${therapistProfile.firstName} ${therapistProfile.lastName}`,
        }).unwrap();
      } else {
        await createTask({
          clinicId,
          taskData,
          creatorId: therapistId,
          creatorName: therapistName,
        }).unwrap();
      }
      handleCloseTaskModal();
      showSuccess(editingTask ? "המשימה עודכנה בהצלחה" : "המשימה נוצרה בהצלחה");
    } catch (error) {
      console.error("[CK-4001] Error saving task:", {
        errorCode: "CK-4001",
        errorMessage: error.message,
        clinicId,
        taskId: editingTask?.id,
        isEditing: !!editingTask,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת המשימה");
    }
  };

  const handleSaveReminder = async () => {
    if (!reminderForm.title.trim()) return;

    // For editing clinic reminders, keep the scope; for new reminders, always personal
    const reminderData = {
      title: reminderForm.title.trim(),
      content: reminderForm.content || "",
      categoryId: reminderForm.categoryId || null,
      scope: editingReminder?.scope || "personal",
    };

    try {
      if (editingReminder) {
        await updateReminder({
          clinicId,
          reminderId: editingReminder.id,
          reminderData,
          editorId: therapistId,
          editorName: `${therapistProfile.firstName} ${therapistProfile.lastName}`,
        }).unwrap();
      } else {
        await createReminder({
          clinicId,
          reminderData,
          creatorId: therapistId,
          creatorName: therapistName,
        }).unwrap();
      }
      handleCloseReminderModal();
      showSuccess(editingReminder ? "הפתק עודכן בהצלחה" : "הפתק נוצר בהצלחה");
    } catch (error) {
      console.error("[CK-4002] Error saving reminder:", {
        errorCode: "CK-4002",
        errorMessage: error.message,
        clinicId,
        reminderId: editingReminder?.id,
        isEditing: !!editingReminder,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת הפתק");
    }
  };

  const handleDeleteTask = (task) => {
    setConfirmDialog({
      isOpen: true,
      type: "danger",
      icon: "delete",
      title: "מחיקת משימה",
      message: `האם אתה בטוח שברצונך למחוק את המשימה "${task.title}"?`,
      onConfirm: async () => {
        try {
          await deleteTask({
            clinicId,
            taskId: task.id,
            taskTitle: task.title,
            therapistId,
            therapistName,
          }).unwrap();
        } catch (error) {
          console.error("[CK-4003] Error deleting task:", {
            errorCode: "CK-4003",
            errorMessage: error.message,
            clinicId,
            taskId: task.id,
            timestamp: new Date().toISOString(),
          });
        }
      },
    });
  };

  const handleDeleteReminder = (reminder) => {
    setConfirmDialog({
      isOpen: true,
      type: "danger",
      icon: "delete",
      title: "מחיקת פתק",
      message: `האם אתה בטוח שברצונך למחוק את הפתק "${reminder.title}"?`,
      onConfirm: async () => {
        try {
          await deleteReminder({
            clinicId,
            reminderId: reminder.id,
            reminderTitle: reminder.title,
            therapistId,
            therapistName,
          }).unwrap();
        } catch (error) {
          console.error("[CK-4004] Error deleting reminder:", {
            errorCode: "CK-4004",
            errorMessage: error.message,
            clinicId,
            reminderId: reminder.id,
            timestamp: new Date().toISOString(),
          });
        }
      },
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
  };

  const handleQuickStatusChange = async (task, newStatus) => {
    try {
      await updateTask({
        clinicId,
        taskId: task.id,
        taskData: { ...task, status: newStatus },
        editorId: therapistId,
        editorName: `${therapistProfile.firstName} ${therapistProfile.lastName}`,
      }).unwrap();
    } catch (error) {
      console.error("[CK-4005] Error updating task status:", {
        errorCode: "CK-4005",
        errorMessage: error.message,
        clinicId,
        taskId: task.id,
        newStatus,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleShowHistory = (item) => {
    setHistoryData(item);
    setShowHistoryModal(true);
  };

  const getCategoryById = (categoryId, type) => {
    const categories = type === "task" ? taskCategories : reminderCategories;
    return categories.find((c) => c.id === categoryId);
  };

  // =====================================================
  // FORMS HANDLERS
  // =====================================================

  // Format date for forms display
  const formatFormDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("he-IL", { day: "numeric", month: "short", year: "numeric" });
  };

  const formatFormDateTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("he-IL", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  // Open send form modal
  const handleOpenSendFormModal = (form) => {
    setSendingForm(form);
    setSelectedPatientId("");
    setPatientSearchQuery("");
    setShowAllClinicPatients(false); // Reset toggle to "my patients"
    setSmsTemplate(clinicProfile?.signatureSmsTemplate || DEFAULT_SIGNATURE_SMS_TEMPLATE);
    setShowSendFormModal(true);
  };

  // Open the send-for-signature modal for a form handed off from the editor
  useEffect(() => {
    if (!pendingSendFormId || isLoadingForms) return;
    const form = forms.find((f) => f.id === pendingSendFormId);
    setPendingSendFormId(null);
    if (form) {
      handleOpenSendFormModal(form);
    } else {
      showError("הטופס לא נמצא");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSendFormId, isLoadingForms, forms]);

  const handleCloseSendFormModal = () => {
    setShowSendFormModal(false);
    setSendingForm(null);
    setSelectedPatientId("");
    setPatientSearchQuery("");
    setMissingPatientFields([]);
  };

  const handleSendFormForSignature = async () => {
    if (!sendingForm || !selectedPatientId) return;
    const selectedPatient = patients.find((p) => p.id === selectedPatientId);
    if (selectedPatient) {
      const validation = validatePatientFieldsForForm(sendingForm.content, selectedPatient, clinicExtraData);
      if (!validation.isValid) {
        setMissingPatientFields(validation.missingFields);
        return;
      }
    }
    try {
      const result = await sendFormForSignature({
        clinicId, formId: sendingForm.id, patientId: selectedPatientId, messageTemplate: smsTemplate,
        systemForm: sendingForm.id.startsWith("sys_form_")
          ? { name: sendingForm.name, content: sendingForm.content }
          : null,
      }).unwrap();
      if (result.success) {
        showSuccess("הטופס נשלח בהצלחה לחתימה");
        handleCloseSendFormModal();
      } else {
        showError(result.error || "שגיאה בשליחת הטופס");
      }
    } catch (error) {
      console.error("[CK-6050] Error sending form for signature:", error);
      showError("שגיאה בשליחת הטופס לחתימה");
    }
  };

  const handleCancelSignatureLink = (link) => {
    setConfirmDialog({
      isOpen: true, type: "warning", icon: "warning",
      title: "ביטול קישור חתימה",
      message: `האם אתה בטוח שברצונך לבטל את הקישור לחתימה עבור "${link.patientName}"?`,
      onConfirm: async () => {
        try {
          await cancelSignatureLink({ clinicId, linkId: link.id }).unwrap();
          showSuccess("הקישור בוטל בהצלחה");
        } catch (error) {
          console.error("[CK-6051] Error cancelling signature link:", error);
          showError("שגיאה בביטול הקישור");
        }
      },
    });
  };

  const handleDeleteSignatureLink = (link) => {
    setConfirmDialog({
      isOpen: true, type: "danger", icon: "delete",
      title: "מחיקת קישור חתימה",
      message: `האם אתה בטוח שברצונך למחוק את הקישור לחתימה עבור "${link.patientName}"?`,
      onConfirm: async () => {
        try {
          await deleteSignatureLink({ clinicId, linkId: link.id, therapistId, therapistName }).unwrap();
          showSuccess("הקישור נמחק בהצלחה");
        } catch (error) {
          console.error("[CK-6052] Error deleting signature link:", error);
          showError("שגיאה במחיקת הקישור");
        }
      },
    });
  };

  const handleCopySignatureLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link.signatureUrl);
      showSuccess("הקישור הועתק ללוח");
    } catch (error) {
      showError("שגיאה בהעתקת הקישור");
    }
  };

  // Questionnaire links store questionnaireUrl on the doc (set by the send
  // Cloud Function); older links fall back to the canonical public route.
  const handleCopyQuestionnaireLink = async (link) => {
    try {
      const url =
        link.questionnaireUrl ||
        `https://clinikit.co.il/questionnaire/${link.id}`;
      await navigator.clipboard.writeText(url);
      showSuccess("הקישור הועתק ללוח");
    } catch (error) {
      console.error("[CK-4071] Error copying questionnaire link:", {
        errorCode: "CK-4071",
        errorMessage: error.message,
        linkId: link?.id,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בהעתקת הקישור");
    }
  };

  // Form handlers — forms are created/edited in the dedicated editor page
  // (/dashboard/forms/new, /dashboard/forms/:formId). System forms open there
  // read-only with a "duplicate to edit" action.
  const handleOpenFormModal = (form = null) => {
    if (form) {
      navigate(`/dashboard/forms/${form.id}`);
    } else {
      navigate("/dashboard/forms/new");
    }
  };

  const handleDeleteForm = (form) => {
    setConfirmDialog({
      isOpen: true, type: "danger", icon: "delete",
      title: "מחיקת טופס", message: `האם אתה בטוח שברצונך למחוק את הטופס "${form.name}"?`,
      onConfirm: async () => {
        try {
          await deleteForm({ clinicId, formId: form.id, formName: form.name, therapistId, therapistName }).unwrap();
          showSuccess("הטופס נמחק בהצלחה");
        } catch (error) {
          console.error("[CK-6011] Error deleting form:", error);
          showError("שגיאה במחיקת הטופס");
        }
      },
    });
  };

  const handleDuplicateForm = async (form) => {
    try {
      if (form.isSystem) {
        const created = await createForm({
          clinicId,
          formData: { name: `${form.name} (העתק)`, content: form.content || "", duplicatedFromSystemId: form.id },
          creatorId: therapistId, creatorName: therapistName,
        }).unwrap();
        showSuccess("הטופס שוכפל בהצלחה! כעת ניתן לערוך אותו.");
        if (created?.id) navigate(`/dashboard/forms/${created.id}`);
      } else {
        await duplicateForm({ clinicId, formId: form.id, creatorId: therapistId, creatorName: therapistName }).unwrap();
        showSuccess("הטופס שוכפל בהצלחה");
      }
    } catch (error) {
      console.error("[CK-6012] Error duplicating form:", error);
      showError("שגיאה בשכפול הטופס");
    }
  };

  // Template handlers
  const handleOpenTemplateModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateData({ name: template.name || "", fields: template.fields || [] });
    } else {
      setEditingTemplate(null);
      setTemplateData({ name: "", fields: [] });
    }
    setShowTemplateModal(true);
  };

  const handleCloseTemplateModal = () => {
    setShowTemplateModal(false);
    setShowPreview(false);
    setEditingTemplate(null);
    setTemplateData({ name: "", fields: [] });
  };

  const handleSaveTemplate = async () => {
    if (!templateData.name.trim()) return;
    if (questionnaireFieldsHaveInvalidChoices(templateData.fields)) {
      showError("בשדות בחירה יש להזין לפחות 2 אפשרויות");
      return;
    }
    const sanitizedFields = sanitizeQuestionnaireFields(templateData.fields);
    try {
      if (editingTemplate) {
        await updateTemplate({
          clinicId, templateId: editingTemplate.id,
          templateData: { name: templateData.name.trim(), fields: sanitizedFields },
          editorId: therapistId, editorName: therapistName,
        }).unwrap();
      } else {
        await createTemplate({
          clinicId, templateData: { name: templateData.name.trim(), fields: sanitizedFields },
          creatorId: therapistId, creatorName: therapistName,
        }).unwrap();
      }
      handleCloseTemplateModal();
      showSuccess(editingTemplate ? "התבנית עודכנה בהצלחה" : "התבנית נוצרה בהצלחה");
    } catch (error) {
      console.error("[CK-6020] Error saving template:", error);
      showError("שגיאה בשמירת התבנית");
    }
  };

  const handleDeleteTemplate = (template) => {
    setConfirmDialog({
      isOpen: true, type: "danger", icon: "delete",
      title: "מחיקת תבנית", message: `האם אתה בטוח שברצונך למחוק את התבנית "${template.name}"?`,
      onConfirm: async () => {
        try {
          await deleteTemplate({ clinicId, templateId: template.id, templateName: template.name, therapistId, therapistName }).unwrap();
          showSuccess("התבנית נמחקה בהצלחה");
        } catch (error) {
          console.error("[CK-6021] Error deleting template:", error);
          showError("שגיאה במחיקת התבנית");
        }
      },
    });
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      if (template.isSystem) {
        await createTemplate({
          clinicId,
          templateData: { name: `${template.name} (העתק)`, fields: template.fields || [], duplicatedFromSystemId: template.id },
          creatorId: therapistId, creatorName: therapistName,
        }).unwrap();
        showSuccess("התבנית שוכפלה בהצלחה! כעת ניתן לערוך אותה.");
      } else {
        await duplicateTemplate({ clinicId, templateId: template.id, creatorId: therapistId, creatorName: therapistName }).unwrap();
        showSuccess("התבנית שוכפלה בהצלחה");
      }
    } catch (error) {
      console.error("[CK-6022] Error duplicating template:", error);
      showError("שגיאה בשכפול התבנית");
    }
  };

  const handleShowPreview = () => setShowPreview(true);

  // Questionnaire handlers
  const handleOpenQuestionnaireModal = (questionnaire = null) => {
    if (questionnaire) {
      setEditingQuestionnaire(questionnaire);
      setQuestionnaireData({
        name: questionnaire.name || "",
        fields: questionnaire.fields || [],
        requireSignature: questionnaire.requireSignature === true,
      });
    } else {
      setEditingQuestionnaire(null);
      setQuestionnaireData({ name: "", fields: [], requireSignature: false });
    }
    setQuestionnairePreviewMode(false);
    setShowQuestionnaireModal(true);
  };

  const handleCloseQuestionnaireModal = () => {
    setShowQuestionnaireModal(false);
    setEditingQuestionnaire(null);
    setQuestionnaireData({ name: "", fields: [], requireSignature: false });
    setQuestionnairePreviewMode(false);
  };

  const handleSaveQuestionnaire = async () => {
    if (!questionnaireData.name.trim()) return;
    if (questionnaireData.fields.length === 0) {
      showError("יש להוסיף לפחות שאלה אחת");
      return;
    }
    if (questionnaireFieldsHaveInvalidChoices(questionnaireData.fields)) {
      showError("בשאלות בחירה יש להזין לפחות 2 אפשרויות");
      return;
    }
    const sanitizedFields = sanitizeQuestionnaireFields(questionnaireData.fields);
    // Always persist an explicit boolean so editing a legacy questionnaire
    // can turn the signature requirement on (or back off).
    const payload = {
      name: questionnaireData.name.trim(),
      fields: sanitizedFields,
      requireSignature: questionnaireData.requireSignature === true,
    };
    try {
      if (editingQuestionnaire) {
        await updateQuestionnaire({
          clinicId, questionnaireId: editingQuestionnaire.id,
          questionnaireData: payload,
          editorId: therapistId, editorName: therapistName,
        }).unwrap();
        showSuccess("השאלון נשמר בהצלחה");
      } else {
        await createQuestionnaire({
          clinicId, questionnaireData: payload,
          creatorId: therapistId, creatorName: therapistName,
        }).unwrap();
        showSuccess("השאלון נוצר בהצלחה");
      }
      handleCloseQuestionnaireModal();
    } catch (error) {
      console.error("[CK-6100] Error saving questionnaire:", error);
      showError("שגיאה בשמירת השאלון");
    }
  };

  const handleOpenSendQuestionnaireModal = (questionnaire) => {
    setSendingQuestionnaire(questionnaire);
    setSelectedQuestionnairePatients([]);
    setQuestionnairePatientSearch("");
    setShowAllClinicPatients(false); // Reset toggle to "my patients"
    setShowSendQuestionnaireModal(true);
  };

  const handleCloseSendQuestionnaireModal = () => {
    setShowSendQuestionnaireModal(false);
    setSendingQuestionnaire(null);
    setSelectedQuestionnairePatients([]);
    setQuestionnairePatientSearch("");
  };

  const handleToggleQuestionnairePatient = (patientId) => {
    setSelectedQuestionnairePatients((prev) =>
      prev.includes(patientId) ? prev.filter((id) => id !== patientId) : [...prev, patientId]
    );
  };

  const handleSelectAllQuestionnairePatients = () => {
    const filteredIds = filteredQuestionnairePatients.map((p) => p.id);
    setSelectedQuestionnairePatients((prev) => Array.from(new Set([...prev, ...filteredIds])));
  };

  const handleClearQuestionnairePatients = () => setSelectedQuestionnairePatients([]);

  const handleSendQuestionnaire = async () => {
    if (!sendingQuestionnaire || selectedQuestionnairePatients.length === 0) {
      showError("יש לבחור לפחות מטופל אחד");
      return;
    }
    try {
      const result = await sendQuestionnaireToPatients({
        clinicId, questionnaireId: sendingQuestionnaire.id, patientIds: selectedQuestionnairePatients,
      }).unwrap();
      if (result.success) {
        showSuccess(`השאלון נשלח ל-${result.sentCount} מטופלים`);
        if (result.failedCount > 0) showError(`נכשל עבור ${result.failedCount} מטופלים`);
        handleCloseSendQuestionnaireModal();
      }
    } catch (error) {
      console.error("[CK-6101] Error sending questionnaire:", error);
      if (typeof error?.message === "string" && error.message.includes("Questionnaire not found")) {
        showError("השאלון נמחק. רענון הרשימה...");
        handleCloseSendQuestionnaireModal();
        refetchQuestionnaires();
      } else {
        showError("שגיאה בשליחת השאלון");
      }
    }
  };

  const handleDuplicateQuestionnaire = async (questionnaire) => {
    try {
      if (questionnaire.isSystem) {
        await createQuestionnaire({
          clinicId,
          questionnaireData: {
            name: `${questionnaire.name} (העתק)`,
            fields: questionnaire.fields || [],
            requireSignature: questionnaire.requireSignature === true,
            duplicatedFromSystemId: questionnaire.id,
          },
          creatorId: therapistId, creatorName: therapistName,
        }).unwrap();
        showSuccess("השאלון שוכפל בהצלחה! כעת ניתן לערוך אותו.");
      } else {
        await duplicateQuestionnaire({
          clinicId, questionnaireId: questionnaire.id, creatorId: therapistId, creatorName: therapistName,
        }).unwrap();
        showSuccess("השאלון שוכפל בהצלחה");
      }
    } catch (error) {
      console.error("[CK-6103] Error duplicating questionnaire:", error);
      showError("שגיאה בשכפול השאלון");
    }
  };

  const handleDeleteQuestionnaire = (questionnaire) => {
    setConfirmDialog({
      isOpen: true, type: "danger", icon: "delete",
      title: "מחיקת שאלון",
      message: `האם אתה בטוח שברצונך למחוק את השאלון "${questionnaire.name}"?`,
      onConfirm: async () => {
        try {
          await deleteQuestionnaire({
            clinicId, questionnaireId: questionnaire.id, questionnaireName: questionnaire.name, therapistId, therapistName,
          }).unwrap();
          showSuccess("השאלון נמחק");
        } catch (error) {
          console.error("[CK-6102] Error deleting questionnaire:", error);
          showError("שגיאה במחיקת השאלון");
        }
      },
    });
  };

  // Handle a click on the task card body → open the edit modal (all fields editable
  // there). Ignore clicks that come from a drag, or from the inline controls.
  const handleTaskCardClick = (task) => {
    // Ignore the click that a drag gesture leaves behind (self-expiring guard).
    if (Date.now() - lastTaskDragEndRef.current < 300) return;
    handleOpenTaskModal(task);
  };

  // Render task card
  const renderTaskCard = (task, isClinicTask = false, draggable = false) => {
    const statusConfig = TASK_STATUSES.find((s) => s.value === task.status);
    const category = getCategoryById(task.categoryId, "task");
    const isPastDue = isDueDatePast(task.dueDate, task.hasDueTime) && task.status !== "done";
    const isDragEnabled = draggable && canReorderTasks;

    return (
      <div
        key={task.id}
        data-task-id={task.id}
        ref={isDragEnabled ? (el) => setTaskCardRef(task.id, el) : undefined}
        className={`task-card ${isClinicTask ? "clinic-task" : ""} ${
          task.status === "done" ? "completed" : ""
        } ${dragTaskId === task.id ? "dragging" : ""}`}
        role="button"
        tabIndex={0}
        onClick={() => handleTaskCardClick(task)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpenTaskModal(task);
          }
        }}
      >
        {/* Drag handle (manual reorder) */}
        {isDragEnabled && (
          <div
            className="task-drag-handle"
            title="גרור לשינוי סדר"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => handleTaskDragStart(e, task.id)}
          >
            <DotsSixVertical size={18} weight="bold" />
          </div>
        )}

        {/* Status Indicator */}
        <div
          className="task-status-indicator"
          style={{ backgroundColor: statusConfig?.color }}
        />

        {/* Task Content */}
        <div className="task-content">
          <div className="task-header">
            <h3 className="task-title">{task.title}</h3>
            {category && (
              <span
                className="task-category"
                style={{
                  backgroundColor: category.color + "20",
                  color: category.color,
                }}
              >
                <Tag size={12} weight="bold" />
                {category.name}
              </span>
            )}
          </div>

          {task.note && (
            <p className="task-note">
              <ChatCircleText size={14} weight="regular" />
              {task.note}
            </p>
          )}

          <div className="task-meta">
            {task.dueDate && (
              <span className={`task-due ${isPastDue ? "past-due" : ""}`}>
                <CalendarBlank size={14} weight="regular" />
                {formatDate(task.dueDate, !!task.hasDueTime)}
              </span>
            )}
            {isClinicTask && (
              <span className="task-clinic-badge">
                <Buildings size={14} weight="regular" />
                משימת קליניקה
              </span>
            )}
          </div>
        </div>

        {/* Quick Status Change */}
        <div className="task-status-select" onClick={(e) => e.stopPropagation()}>
          <select
            value={task.status}
            onChange={(e) => handleQuickStatusChange(task, e.target.value)}
          >
            {TASK_STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
          <CaretDown size={14} weight="bold" className="select-icon" />
        </div>

        {/* Actions */}
        <div className="task-actions" onClick={(e) => e.stopPropagation()}>
          {task.editHistory?.length > 0 && (
            <button
              className="action-btn"
              onClick={() => handleShowHistory(task)}
              title="היסטוריית עריכה"
            >
              <ClockCounterClockwise size={18} weight="regular" />
            </button>
          )}
          <button
            className="action-btn"
            onClick={() => handleOpenTaskModal(task)}
            title="ערוך"
          >
            <Pencil size={18} weight="regular" />
          </button>
          <button
            className="action-btn delete"
            onClick={() => handleDeleteTask(task)}
            title="מחק"
          >
            <Trash size={18} weight="regular" />
          </button>
        </div>
      </div>
    );
  };

  // Render reminder card
  const renderReminderCard = (reminder, isClinicReminder = false) => {
    const category = getCategoryById(reminder.categoryId, "reminder");

    return (
      <div
        key={reminder.id}
        className={`reminder-card ${isClinicReminder ? "clinic-reminder" : ""}`}
      >
        <div className="reminder-header">
          <h3 className="reminder-title">{reminder.title}</h3>
          {category && (
            <span
              className="reminder-category"
              style={{
                backgroundColor: category.color + "20",
                color: category.color,
              }}
            >
              <Tag size={12} weight="bold" />
              {category.name}
            </span>
          )}
        </div>

        {reminder.content && (
          <div
            className="reminder-content rich-text-display"
            dangerouslySetInnerHTML={{ __html: reminder.content }}
          />
        )}

        {isClinicReminder && (
          <div className="reminder-meta">
            <span className="reminder-clinic-badge">
              <Buildings size={14} weight="regular" />
              פתק קליניקה
            </span>
          </div>
        )}

        <div className="reminder-footer">
          <span className="reminder-date">
            {formatDate(reminder.createdAt)}
          </span>
          <div className="reminder-actions">
            {reminder.editHistory?.length > 0 && (
              <button
                className="action-btn"
                onClick={() => handleShowHistory(reminder)}
                title="היסטוריית עריכה"
              >
                <ClockCounterClockwise size={16} weight="regular" />
              </button>
            )}
            <button
              className="action-btn"
              onClick={() => handleOpenReminderModal(reminder)}
              title="ערוך"
            >
              <Pencil size={16} weight="regular" />
            </button>
            <button
              className="action-btn delete"
              onClick={() => handleDeleteReminder(reminder)}
              title="מחק"
            >
              <Trash size={16} weight="regular" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="tasks-reminders-page toolbox-page">
      {/* Header */}
      <PageHeader
        icon={Toolbox}
        title="ארגז הכלים"
        actions={<HelpButton featureKey="toolbox" />}
      />

      {/* Board card: tab strip + content share one elevated surface (2026) */}
      <div className="tb-board">
      {/* Main tabs — flat strip on desktop, native select on mobile.
          .toolbox-tabs / .main-tab are onboarding-tour anchors (clicked by text). */}
      <TabBar
        className="main-tabs toolbox-tabs"
        tabClassName="main-tab"
        ariaLabel="מעבר בין כלים"
        activeId={activeMainTab}
        onChange={(id) => { setActiveMainTab(id); setSearchQuery(""); }}
        tabs={[
          { id: "tasks", label: "משימות", icon: ListChecks },
          { id: "reminders", label: "פתקים", icon: Note },
          { id: "leads", label: "מתעניינים", icon: UserPlus },
          { id: "forms", label: "טפסים", icon: FileText, badge: pendingLinks.length || 0 },
          { id: "questionnaires", label: "שאלונים", icon: ListBullets, badge: pendingQuestionnaireLinks.length || 0 },
          { id: "templates", label: "תבניות", icon: GridFour },
          { id: "myFolder", label: "התיקייה שלי", icon: FolderSimple },
        ]}
      />

      {/* Content Area */}
      <div className="content-area">
        {/* My Folder tab has its own header inside the component */}
        {activeMainTab === "myFolder" ? (
          <ClinicFilesTab />
        ) : activeMainTab === "leads" ? (
          /* Leads v2: self-contained clinic-wide tab (header, filters, views, modals) */
          <LeadsTab hasAccess={canViewAllLeads} deepLink={leadsDeepLink} onDeepLinkConsumed={() => setLeadsDeepLink(null)} />
        ) : (
        <>
        {/* Header with Search and Add Button */}
        <div className="content-header">
          {/* Search box for forms/questionnaires/templates tabs */}
          {(activeMainTab === "forms" || activeMainTab === "questionnaires" || activeMainTab === "templates") ? (
            <div className="search-box">
              <MagnifyingGlass size={18} weight="regular" />
              <input
                type="text"
                placeholder={
                  activeMainTab === "forms" ? "חיפוש טפסים..." :
                  activeMainTab === "questionnaires" ? "חיפוש שאלונים..." :
                  "חיפוש תבניות..."
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="clear-search" onClick={() => setSearchQuery("")}>
                  <X size={14} weight="bold" />
                </button>
              )}
            </div>
          ) : (
            <div className="content-header-spacer" />
          )}
          {!clinicProfile?.isSoloClinic && (
            <HoverTip
              text={
                ["tasks", "reminders"].includes(activeMainTab)
                  ? "המידע כאן אישי — למטפלים אחרים בקליניקה אין גישה אליו"
                  : "מידע זה משותף עם שאר המטפלים בקליניקה"
              }
            >
              <span
                className={`privacy-tag ${["tasks", "reminders"].includes(activeMainTab) ? "privacy-personal" : "privacy-shared"}`}
                tabIndex={0}
              >
                <Eye size={13} weight="bold" />
                <span>
                  {["tasks", "reminders"].includes(activeMainTab) ? "אישי" : "משותף"}
                </span>
              </span>
            </HoverTip>
          )}
          <div className="content-header-actions">
            <HelpButton featureKey={`${activeMainTab}_tab`} />
            <CustomClinikitActionButton
              icon={<Plus size={16} weight="bold" />}
              onClick={() => {
                if (activeMainTab === "tasks") handleOpenTaskModal();
                else if (activeMainTab === "reminders") handleOpenReminderModal();
                else if (activeMainTab === "forms") handleOpenFormModal();
                else if (activeMainTab === "questionnaires") handleOpenQuestionnaireModal();
                else if (activeMainTab === "templates") handleOpenTemplateModal();
              }}
            >
              {activeMainTab === "tasks" ? "משימה חדשה" :
               activeMainTab === "reminders" ? "פתק חדש" :
               activeMainTab === "forms" ? "טופס חדש" :
               activeMainTab === "questionnaires" ? "שאלון חדש" :
               "תבנית חדשה"}
            </CustomClinikitActionButton>
          </div>
        </div>

        {/* Tasks View */}
        {activeMainTab === "tasks" && (
          <div className="tasks-section">
            {/* Status Filter */}
            <div className="status-filter">
              <button
                className={`filter-btn ${
                  statusFilter === "all" ? "active" : ""
                }`}
                onClick={() => setStatusFilter("all")}
              >
                הכל ({taskCounts.all})
              </button>
              {TASK_STATUSES.map((status) => (
                <button
                  key={status.value}
                  className={`filter-btn ${
                    statusFilter === status.value ? "active" : ""
                  }`}
                  onClick={() => setStatusFilter(status.value)}
                  style={{ "--filter-color": status.color }}
                >
                  <status.icon size={14} weight="bold" />
                  <span>{status.label}</span>
                  <span className="filter-count">
                    ({taskCounts[status.value]})
                  </span>
                </button>
              ))}
            </div>

            {/* Tasks List */}
            {isLoadingPersonalTasks ? (
              <ContentLoader message="טוען משימות..." />
            ) : filteredPersonalTasks.length === 0 &&
              filteredMyClinicTasks.length === 0 ? (
              <div className="empty-state">
                <EmptyDraw variant={statusFilter !== "all" ? "search" : "task"} />
                <p>{statusFilter !== "all" ? "אין משימות בסטטוס זה" : "נהל את המשימות היומיומיות שלך — מעקב טיפול, שיחות, תיאומים ועוד."}</p>
                <button
                  className="add-btn-secondary"
                  onClick={() => handleOpenTaskModal()}
                >
                  <Plus size={18} weight="bold" />
                  <span>הוסף משימה ראשונה</span>
                </button>
              </div>
            ) : (
              <>
                {/* Personal Tasks */}
                {filteredPersonalTasks.length > 0 && (
                  <div className="tasks-list">
                    {displayedPersonalTasks.map((task) =>
                      renderTaskCard(task, false, true)
                    )}
                  </div>
                )}

                {/* My Clinic Tasks Section */}
                {filteredMyClinicTasks.length > 0 && (
                  <div className="my-clinic-tasks-section">
                    <div className="section-divider">
                      <Buildings size={18} weight="bold" />
                      <span>משימות קליניקה שהוקצו לי</span>
                      <span className="count-badge">
                        {filteredMyClinicTasks.length}
                      </span>
                    </div>
                    <div className="tasks-list">
                      {filteredMyClinicTasks.map((task) =>
                        renderTaskCard(task, true)
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Reminders View */}
        {activeMainTab === "reminders" && (
          <div className="reminders-section">
            {isLoadingPersonalReminders ? (
              <ContentLoader message="טוען פתקים..." />
            ) : personalReminders.length === 0 &&
              myClinicReminders.length === 0 ? (
              <div className="empty-state">
                <EmptyDraw variant="reminder" />
                <p>צור פתקים כדי לא לשכוח דברים חשובים — שיחה עם הורה, חידוש ביטוח, מעקב מטופל ועוד.</p>
                <button
                  className="add-btn-secondary"
                  onClick={() => handleOpenReminderModal()}
                >
                  <Plus size={18} weight="bold" />
                  <span>הוסף פתק ראשון</span>
                </button>
              </div>
            ) : (
              <>
                {/* Personal Reminders */}
                {personalReminders.length > 0 && (
                  <div className="reminders-grid">
                    {personalReminders.map((reminder) =>
                      renderReminderCard(reminder, false)
                    )}
                  </div>
                )}

                {/* My Clinic Reminders Section */}
                {myClinicReminders.length > 0 && (
                  <div className="my-clinic-reminders-section">
                    <div className="section-divider">
                      <Buildings size={18} weight="bold" />
                      <span>פתקי קליניקה שלי</span>
                      <span className="count-badge">
                        {myClinicReminders.length}
                      </span>
                    </div>
                    <div className="reminders-grid">
                      {myClinicReminders.map((reminder) =>
                        renderReminderCard(reminder, true)
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Forms View */}
        {activeMainTab === "forms" && (
          <div className="items-section forms-section">
            {isLoadingForms ? (
              <ContentLoader message="טוען טפסים..." />
            ) : filteredForms.length === 0 && filteredPendingLinks.length === 0 ? (
              <div className="empty-state">
                <EmptyDraw variant={searchQuery ? "search" : "form"} />
                <p>{searchQuery ? "לא נמצאו טפסים" : "צור טופס פעם אחת (הסכם טיפול, הצהרת בריאות וכו׳) ושלח אותו למטופלים לחתימה דיגיטלית."}</p>
                {!searchQuery && (
                  <button className="add-btn-secondary" onClick={() => handleOpenFormModal()}>
                    <Plus size={18} weight="bold" />
                    <span>צור טופס ראשון</span>
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Forms Table */}
                {filteredForms.length > 0 && (
                  <div className="forms-table-wrap">
                    <table className="forms-table">
                      <thead>
                        <tr>
                          <th className="ft-col-name">שם הטופס</th>
                          <th className="ft-col-source">מקור</th>
                          <th className="ft-col-date">נוצר</th>
                          <th className="ft-col-sig">חתימות לקוח</th>
                          <th className="ft-col-actions">פעולות</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredForms.map((form) => {
                          const canEdit = canEditItem(form);
                          const slotCount = countSignatureSlots(form.content);
                          const openLabel = form.isSystem ? "צפייה בטופס" : canEdit ? "עריכת הטופס" : "צפייה בטופס";
                          return (
                            <tr key={form.id} className={`forms-table-row${form.isSystem ? " is-system" : ""}`} onClick={() => handleOpenFormModal(form)}>
                              <td className="ft-col-name">
                                <div className="ft-name">
                                  <span className="ft-icon"><FileText size={20} weight="duotone" /></span>
                                  <div className="ft-name-text">
                                    <span className="ft-title">{form.name}</span>
                                    {form.isSystem && (
                                      <span className="system-badge ft-badge"><Star size={11} weight="fill" />מובנה</span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="ft-col-source">
                                {form.isSystem ? (
                                  <span className="ft-muted">CliniKit</span>
                                ) : (
                                  <span className="ft-cell-inline"><User size={14} weight="regular" />{form.creatorName}</span>
                                )}
                              </td>
                              <td className="ft-col-date">
                                {form.isSystem ? <span className="ft-muted">—</span> : formatFormDate(form.createdAt)}
                              </td>
                              <td className="ft-col-sig">
                                {slotCount > 0 ? (
                                  <span className="ft-pill"><PencilLine size={13} weight="bold" />{slotCount}</span>
                                ) : (
                                  <HoverTip text="לטופס אין מקום לחתימת לקוח — מומלץ להוסיף לפני שליחה"><span className="ft-muted">ללא</span></HoverTip>
                                )}
                              </td>
                              <td className="ft-col-actions" onClick={(e) => e.stopPropagation()}>
                                <div className="ft-actions">
                                  <HoverTip text="שליחת הטופס למטופל ב-SMS לחתימה דיגיטלית"><button type="button" className="action-btn send-signature" aria-label="שלח לחתימה" onClick={() => handleOpenSendFormModal(form)}><PaperPlaneTilt size={18} weight="regular" /></button></HoverTip>
                                  <HoverTip text={form.isSystem ? "יצירת עותק אישי של הטופס המובנה שניתן לערוך" : "יצירת עותק של הטופס"}><button type="button" className="action-btn" aria-label="שכפל" onClick={() => handleDuplicateForm(form)}><Copy size={18} weight="regular" /></button></HoverTip>
                                  {form.isSystem || !canEdit ? (
                                    <HoverTip text={form.isSystem ? "צפייה בטופס המובנה (לעריכה יש לשכפל)" : "צפייה בטופס (נוצר על ידי מטפל/ת אחר/ת)"}><button type="button" className="action-btn" aria-label={openLabel} onClick={() => handleOpenFormModal(form)}><Eye size={18} weight="regular" /></button></HoverTip>
                                  ) : (
                                    <>
                                      <HoverTip text="פתיחת הטופס בעורך לעריכה"><button type="button" className="action-btn" aria-label="ערוך" onClick={() => handleOpenFormModal(form)}><Pencil size={18} weight="regular" /></button></HoverTip>
                                      <HoverTip text="מחיקת הטופס לצמיתות (טפסים חתומים נשמרים)"><button type="button" className="action-btn delete" aria-label="מחק" onClick={() => handleDeleteForm(form)}><Trash size={18} weight="regular" /></button></HoverTip>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {/* Pending Signatures Section */}
                {filteredPendingLinks.length > 0 && (
                  <div className="pending-signatures-section">
                    <div className="section-header">
                      <Clock size={20} weight="regular" />
                      <h3>ממתינים לחתימה ({filteredPendingLinks.length})</h3>
                    </div>
                    <div className="forms-table-wrap">
                      <table className="forms-table pending-table">
                        <thead>
                          <tr>
                            <th className="ft-col-name">טופס</th>
                            <th>מטופל/ת</th>
                            <th className="ft-col-date">נשלח</th>
                            <th>שולח</th>
                            <th>סטטוס SMS</th>
                            <th className="ft-col-actions">פעולות</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPendingLinks.map((link) => {
                            const canManage = isAdmin || link.therapistId === therapistId;
                            const smsLabel = link.smsStatus === "sent" ? "נשלח" : link.smsStatus === "failed" ? "נכשל" : "ממתין";
                            return (
                              <tr key={link.id} className="forms-table-row is-pending">
                                <td className="ft-col-name">
                                  <div className="ft-name">
                                    <span className="ft-icon ft-icon--pending"><Clock size={20} weight="duotone" /></span>
                                    <span className="ft-title">{link.formName}</span>
                                  </div>
                                </td>
                                <td>
                                  <div className="ft-stack">
                                    <span className="ft-cell-inline"><User size={14} weight="regular" />{link.patientName}</span>
                                    <span className="ft-cell-inline ft-muted"><Phone size={13} weight="regular" /><bdi>{link.patientPhone}</bdi></span>
                                  </div>
                                </td>
                                <td className="ft-col-date">{formatFormDateTime(link.createdAt)}</td>
                                <td><span className="ft-cell-inline">{link.therapistName}</span></td>
                                <td><span className={`sms-status ft-sms ${link.smsStatus || "pending"}`}>{smsLabel}</span></td>
                                <td className="ft-col-actions">
                                  <div className="ft-actions">
                                    <HoverTip text="העתקת קישור החתימה — לשליחה ידנית למטופל"><button type="button" className="action-btn" aria-label="העתק קישור" onClick={() => handleCopySignatureLink(link)}><LinkIcon size={18} weight="regular" /></button></HoverTip>
                                    {canManage && (
                                      <>
                                        <HoverTip text="ביטול הקישור — המטופל לא יוכל לחתום דרכו"><button type="button" className="action-btn warning" aria-label="בטל קישור" onClick={() => handleCancelSignatureLink(link)}><Warning size={18} weight="regular" /></button></HoverTip>
                                        <HoverTip text="מחיקת הבקשה הממתינה"><button type="button" className="action-btn delete" aria-label="מחק" onClick={() => handleDeleteSignatureLink(link)}><Trash size={18} weight="regular" /></button></HoverTip>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Questionnaires View */}
        {activeMainTab === "questionnaires" && (
          <div className="items-section questionnaires-section">
            {isLoadingQuestionnaires ? (
              <ContentLoader message="טוען שאלונים..." />
            ) : filteredQuestionnaires.length === 0 && filteredPendingQuestionnaireLinks.length === 0 ? (
              <div className="empty-state">
                <EmptyDraw variant={searchQuery ? "search" : "questionnaire"} />
                <p>{searchQuery ? "לא נמצאו שאלונים" : "בנה שאלון מותאם אישית ושלח אותו למטופלים. הם ממלאים מהנייד — והתשובות מגיעות אליך מסודרות."}</p>
                {!searchQuery && (
                  <button className="add-btn-secondary" onClick={() => handleOpenQuestionnaireModal()}>
                    <Plus size={18} weight="bold" />
                    <span>צור שאלון ראשון</span>
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Questionnaires Table */}
                {filteredQuestionnaires.length > 0 && (
                  <div className="forms-table-wrap">
                    <table className="forms-table questionnaires-table">
                      <thead>
                        <tr>
                          <th className="ft-col-name">שם השאלון</th>
                          <th className="ft-col-source">מקור</th>
                          <th className="ft-col-date">נוצר</th>
                          <th>שאלות</th>
                          <th className="ft-col-actions">פעולות</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredQuestionnaires.map((questionnaire) => {
                          const fields = questionnaire.fields || [];
                          const fieldCount = fields.length;
                          const requiredCount = fields.filter((f) => f.required).length;
                          const canEdit = canEditItem(questionnaire);
                          const previewLabels = fields.slice(0, 3).map((f) => f.label).filter(Boolean).join(" · ");
                          return (
                            <tr key={questionnaire.id} className={`forms-table-row${questionnaire.isSystem ? " is-system" : ""}`} onClick={() => handleOpenQuestionnaireModal(questionnaire)}>
                              <td className="ft-col-name">
                                <div className="ft-name">
                                  <span className="ft-icon ft-icon--questionnaire"><ListBullets size={20} weight="duotone" /></span>
                                  <div className="ft-name-text">
                                    <span className="ft-title">{questionnaire.name}</span>
                                    {questionnaire.isSystem && (
                                      <span className="system-badge ft-badge"><Star size={11} weight="fill" />מובנה</span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="ft-col-source">
                                {questionnaire.isSystem ? (
                                  <span className="ft-muted">CliniKit</span>
                                ) : (
                                  <span className="ft-cell-inline"><User size={14} weight="regular" />{questionnaire.creatorName}</span>
                                )}
                              </td>
                              <td className="ft-col-date">
                                {questionnaire.isSystem ? <span className="ft-muted">—</span> : formatFormDate(questionnaire.createdAt)}
                              </td>
                              <td>
                                <HoverTip text={previewLabels ? `שאלות: ${previewLabels}${fieldCount > 3 ? ` +${fieldCount - 3} נוספות` : ""}` : "השאלון ריק"}>
                                  <span className="ft-pill ft-pill--neutral">
                                    <ListBullets size={13} weight="bold" />
                                    {fieldCount}
                                    {requiredCount > 0 && <span className="ft-pill-sub">· {requiredCount} חובה</span>}
                                  </span>
                                </HoverTip>
                                {questionnaire.requireSignature === true && (
                                  <HoverTip text="המטופל/ת נדרשים לחתום דיגיטלית לפני שליחת השאלון">
                                    <span className="ft-pill ft-pill--signature">
                                      <Signature size={13} weight="bold" />
                                      חתימה
                                    </span>
                                  </HoverTip>
                                )}
                              </td>
                              <td className="ft-col-actions" onClick={(e) => e.stopPropagation()}>
                                <div className="ft-actions">
                                  {!questionnaire.isSystem && (
                                    <HoverTip text="שליחת השאלון למטופלים ב-SMS למילוי"><button type="button" className="action-btn send-signature" aria-label="שלח למטופלים" onClick={() => handleOpenSendQuestionnaireModal(questionnaire)}><PaperPlaneTilt size={18} weight="regular" /></button></HoverTip>
                                  )}
                                  <HoverTip text={questionnaire.isSystem ? "יצירת עותק אישי של השאלון המובנה שניתן לערוך" : "יצירת עותק של השאלון"}><button type="button" className="action-btn" aria-label="שכפל" onClick={() => handleDuplicateQuestionnaire(questionnaire)}><Copy size={18} weight="regular" /></button></HoverTip>
                                  {questionnaire.isSystem || !canEdit ? (
                                    <HoverTip text={questionnaire.isSystem ? "צפייה בשאלון המובנה (לעריכה יש לשכפל)" : "צפייה בשאלון (נוצר על ידי מטפל/ת אחר/ת)"}><button type="button" className="action-btn" aria-label="צפייה בשאלון" onClick={() => handleOpenQuestionnaireModal(questionnaire)}><Eye size={18} weight="regular" /></button></HoverTip>
                                  ) : (
                                    <>
                                      <HoverTip text="עריכת השאלון ושדותיו"><button type="button" className="action-btn" aria-label="ערוך" onClick={() => handleOpenQuestionnaireModal(questionnaire)}><Pencil size={18} weight="regular" /></button></HoverTip>
                                      <HoverTip text="מחיקת השאלון לצמיתות (תשובות שהתקבלו נשמרות)"><button type="button" className="action-btn delete" aria-label="מחק" onClick={() => handleDeleteQuestionnaire(questionnaire)}><Trash size={18} weight="regular" /></button></HoverTip>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {/* Pending Questionnaires Section */}
                {filteredPendingQuestionnaireLinks.length > 0 && (
                  <div className="pending-questionnaires-section pending-signatures-section">
                    <div className="section-header">
                      <Clock size={20} weight="regular" />
                      <h3>ממתינים למענה ({filteredPendingQuestionnaireLinks.length})</h3>
                    </div>
                    <div className="forms-table-wrap">
                      <table className="forms-table pending-table">
                        <thead>
                          <tr>
                            <th className="ft-col-name">שאלון</th>
                            <th>מטופל/ת</th>
                            <th className="ft-col-date">נשלח</th>
                            <th>מטפל/ת</th>
                            <th>סטטוס</th>
                            <th className="ft-col-actions">פעולות</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPendingQuestionnaireLinks.map((link) => {
                            const statusLabel = link.status === "pending" ? "ממתין" : link.status === "viewed" ? "נצפה" : "הושלם";
                            const statusClass = link.status === "viewed" ? "sent" : link.status === "completed" ? "sent" : "pending";
                            return (
                              <tr key={link.id} className="forms-table-row is-pending is-static">
                                <td className="ft-col-name">
                                  <div className="ft-name">
                                    <span className="ft-icon ft-icon--pending"><Clock size={20} weight="duotone" /></span>
                                    <span className="ft-title">{link.questionnaireName}</span>
                                  </div>
                                </td>
                                <td>
                                  <div className="ft-stack">
                                    <span className="ft-cell-inline"><User size={14} weight="regular" />{link.patientName}</span>
                                    <span className="ft-cell-inline ft-muted"><Phone size={13} weight="regular" /><bdi>{link.patientPhone}</bdi></span>
                                  </div>
                                </td>
                                <td className="ft-col-date">{formatFormDateTime(link.createdAt)}</td>
                                <td><span className="ft-cell-inline">{link.therapistName}</span></td>
                                <td>
                                  <HoverTip text={link.status === "viewed" ? "המטופל פתח את השאלון אך טרם שלח תשובות" : link.status === "pending" ? "הקישור נשלח — המטופל טרם פתח אותו" : "השאלון מולא ונשלח"}>
                                    <span className={`ft-sms ${statusClass}`}>{statusLabel}</span>
                                  </HoverTip>
                                </td>
                                <td className="ft-col-actions">
                                  <div className="ft-actions">
                                    <HoverTip text="העתקת קישור השאלון — לשליחה ידנית למטופל"><button type="button" className="action-btn" aria-label="העתק קישור" onClick={() => handleCopyQuestionnaireLink(link)}><LinkIcon size={18} weight="regular" /></button></HoverTip>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Templates View */}
        {activeMainTab === "templates" && (
          <div className="items-section templates-section">
            {isLoadingTemplates ? (
              <ContentLoader message="טוען תבניות..." />
            ) : filteredTemplates.length === 0 ? (
              <div className="empty-state">
                <EmptyDraw variant={searchQuery ? "search" : "template"} />
                <p>{searchQuery ? "לא נמצאו תבניות" : "צור תבנית תיעוד פעם אחת — הגדר שדות כמו מטרת הטיפול, הערות, המלצות — והשתמש בה שוב ושוב בכל מפגש."}</p>
                {!searchQuery && (
                  <button className="add-btn-secondary" onClick={() => handleOpenTemplateModal()}>
                    <Plus size={18} weight="bold" />
                    <span>צור תבנית ראשונה</span>
                  </button>
                )}
              </div>
            ) : (
              <div className="forms-table-wrap">
                <table className="forms-table templates-table">
                  <thead>
                    <tr>
                      <th className="ft-col-name">שם התבנית</th>
                      <th className="ft-col-source">מקור</th>
                      <th className="ft-col-date">נוצר</th>
                      <th>שדות</th>
                      <th className="ft-col-actions">פעולות</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTemplates.map((template) => {
                      const canEdit = canEditItem(template);
                      const fields = template.fields || [];
                      const fieldCount = fields.length;
                      const requiredCount = fields.filter((f) => f.required).length;
                      const previewLabels = fields.slice(0, 3).map((f) => f.label).filter(Boolean).join(" · ");
                      return (
                        <tr key={template.id} className={`forms-table-row${template.isSystem ? " is-system" : ""}`} onClick={() => handleOpenTemplateModal(template)}>
                          <td className="ft-col-name">
                            <div className="ft-name">
                              <span className="ft-icon ft-icon--template"><GridFour size={20} weight="duotone" /></span>
                              <div className="ft-name-text">
                                <span className="ft-title">{template.name}</span>
                                {template.isSystem && (
                                  <span className="system-badge ft-badge"><Star size={11} weight="fill" />מובנה</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="ft-col-source">
                            {template.isSystem ? (
                              <span className="ft-muted">CliniKit</span>
                            ) : (
                              <span className="ft-cell-inline"><User size={14} weight="regular" />{template.creatorName}</span>
                            )}
                          </td>
                          <td className="ft-col-date">
                            {template.isSystem ? <span className="ft-muted">—</span> : formatFormDate(template.createdAt)}
                          </td>
                          <td>
                            <HoverTip text={previewLabels ? `שדות: ${previewLabels}${fieldCount > 3 ? ` +${fieldCount - 3} נוספים` : ""}` : "התבנית ריקה"}>
                              <span className="ft-pill ft-pill--neutral">
                                <GridFour size={13} weight="bold" />
                                {fieldCount}
                                {requiredCount > 0 && <span className="ft-pill-sub">· {requiredCount} חובה</span>}
                              </span>
                            </HoverTip>
                          </td>
                          <td className="ft-col-actions" onClick={(e) => e.stopPropagation()}>
                            <div className="ft-actions">
                              <HoverTip text={template.isSystem ? "יצירת עותק אישי של התבנית המובנית שניתן לערוך" : "יצירת עותק של התבנית"}><button type="button" className="action-btn" aria-label="שכפל" onClick={() => handleDuplicateTemplate(template)}><Copy size={18} weight="regular" /></button></HoverTip>
                              {template.isSystem || !canEdit ? (
                                <HoverTip text={template.isSystem ? "צפייה בתבנית המובנית (לעריכה יש לשכפל)" : "צפייה בתבנית (נוצרה על ידי מטפל/ת אחר/ת)"}><button type="button" className="action-btn" aria-label="צפייה בתבנית" onClick={() => handleOpenTemplateModal(template)}><Eye size={18} weight="regular" /></button></HoverTip>
                              ) : (
                                <>
                                  <HoverTip text="עריכת התבנית ושדותיה"><button type="button" className="action-btn" aria-label="ערוך" onClick={() => handleOpenTemplateModal(template)}><Pencil size={18} weight="regular" /></button></HoverTip>
                                  <HoverTip text="מחיקת התבנית לצמיתות (תיעודים קיימים נשמרים)"><button type="button" className="action-btn delete" aria-label="מחק" onClick={() => handleDeleteTemplate(template)}><Trash size={18} weight="regular" /></button></HoverTip>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        </>
        )}
      </div>
      </div>

      {/* Task Modal */}
      {showTaskModal && (
        <div className="modal-overlay" onClick={handleCloseTaskModal}>
          <div
            className="modal task-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="tb-modal-title"><span className="tb-modal-icon"><ListChecks size={18} weight="fill" /></span><h3>{editingTask ? "עריכת משימה" : "משימה חדשה"}</h3></div>
              <div className="modal-header-actions">
                <HelpButton featureKey="task_modal" />
                <ModalCloseButton onClick={handleCloseTaskModal} />
              </div>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="required">כותרת</label>
                <input
                  type="text"
                  value={taskForm.title}
                  onChange={(e) =>
                    setTaskForm({ ...taskForm, title: e.target.value })
                  }
                  placeholder="הכנס כותרת למשימה"
                />
              </div>

              <div className="form-group">
                <label>הערה</label>
                <textarea
                  value={taskForm.note}
                  onChange={(e) =>
                    setTaskForm({ ...taskForm, note: e.target.value })
                  }
                  placeholder="הוסף הערה (אופציונלי)"
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>סטטוס</label>
                  <select
                    value={taskForm.status}
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, status: e.target.value })
                    }
                  >
                    {TASK_STATUSES.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>תאריך יעד</label>
                  <DatePicker
                    value={taskForm.dueDate}
                    onChange={(e) =>
                      setTaskForm({
                        ...taskForm,
                        dueDate: e.target.value,
                        // Clear time when date is cleared
                        dueTime: e.target.value ? taskForm.dueTime : "",
                      })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>שעת יעד</label>
                  <TimePicker
                    value={taskForm.dueTime}
                    disabled={!taskForm.dueDate}
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, dueTime: e.target.value })
                    }
                  />
                </div>
              </div>

              {taskCategories.length > 0 && (
                <div className="form-group">
                  <label>קטגוריה</label>
                  <select
                    value={taskForm.categoryId}
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, categoryId: e.target.value })
                    }
                  >
                    <option value="">ללא קטגוריה</option>
                    {taskCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseTaskModal}>
                ביטול
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveTask}
                disabled={
                  !taskForm.title.trim() || isCreatingTask || isUpdatingTask
                }
              >
                {(isCreatingTask || isUpdatingTask) && (
                  <LoaderLoop size="xs" />
                )}
                <span>{editingTask ? "שמור שינויים" : "צור משימה"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reminder Modal */}
      {showReminderModal && (
        <div className="modal-overlay" onClick={handleCloseReminderModal}>
          <div
            className="modal reminder-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="tb-modal-title"><span className="tb-modal-icon"><Note size={18} weight="fill" /></span><h3>{editingReminder ? "עריכת פתק" : "פתק חדש"}</h3></div>
              <div className="modal-header-actions">
                <HelpButton featureKey="reminder_modal" />
                <ModalCloseButton onClick={handleCloseReminderModal} />
              </div>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="required">כותרת</label>
                <input
                  type="text"
                  value={reminderForm.title}
                  onChange={(e) =>
                    setReminderForm({ ...reminderForm, title: e.target.value })
                  }
                  placeholder="הכנס כותרת לפתק"
                />
              </div>

              {reminderCategories.length > 0 && (
                <div className="form-group">
                  <label>קטגוריה</label>
                  <select
                    value={reminderForm.categoryId}
                    onChange={(e) =>
                      setReminderForm({
                        ...reminderForm,
                        categoryId: e.target.value,
                      })
                    }
                  >
                    <option value="">ללא קטגוריה</option>
                    {reminderCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>תוכן</label>
                <RichTextEditor
                  value={reminderForm.content}
                  onChange={(content) =>
                    setReminderForm({ ...reminderForm, content })
                  }
                  placeholder="הכנס תוכן לפתק..."
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={handleCloseReminderModal}
              >
                ביטול
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveReminder}
                disabled={
                  !reminderForm.title.trim() ||
                  isCreatingReminder ||
                  isUpdatingReminder
                }
              >
                {(isCreatingReminder || isUpdatingReminder) && (
                  <LoaderLoop size="xs" />
                )}
                <span>{editingReminder ? "שמור שינויים" : "צור פתק"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && historyData && (
        <div
          className="modal-overlay"
          onClick={() => setShowHistoryModal(false)}
        >
          <div
            className="modal history-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="tb-modal-title"><span className="tb-modal-icon"><ClockCounterClockwise size={18} weight="fill" /></span><h3>היסטוריית עריכה</h3></div>
              <ModalCloseButton onClick={() => setShowHistoryModal(false)} />
            </div>

            <div className="modal-body">
              <h4 className="history-item-title">{historyData.title}</h4>

              {historyData.editHistory?.length > 0 ? (
                <div className="history-list">
                  {[...historyData.editHistory]
                    .reverse()
                    .map((entry, index) => (
                      <div key={index} className="history-entry">
                        <div className="history-entry-header">
                          <span className="history-editor">
                            {entry.editorName}
                          </span>
                          <span className="history-date">
                            {formatDateTime(entry.editedAt)}
                          </span>
                        </div>
                        <div className="history-changes">
                          {Object.entries(entry.changes.after).map(
                            ([key, value]) => {
                              const before = entry.changes.before[key];
                              if (before === value) return null;

                              const fieldNames = {
                                title: "כותרת",
                                note: "הערה",
                                content: "תוכן",
                                status: "סטטוס",
                                assignedTo: "שיוך",
                                categoryId: "קטגוריה",
                                dueDate: "תאריך יעד",
                              };

                              return (
                                <div key={key} className="change-item">
                                  <span className="change-field">
                                    {fieldNames[key] || key}:
                                  </span>
                                  <span className="change-value">
                                    {before || "(ריק)"} → {value || "(ריק)"}
                                  </span>
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="no-history">אין היסטוריית עריכה</p>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowHistoryModal(false)}
              >
                סגור
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="modal-overlay" onClick={handleCloseTemplateModal}>
          <div className="modal template-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="tb-modal-title"><span className="tb-modal-icon"><GridFour size={18} weight="fill" /></span><h3>{editingTemplate?.isSystem ? "צפייה בתבנית מובנית" : editingTemplate ? "עריכת תבנית" : "תבנית חדשה"}</h3></div>
              <div className="modal-header-actions">
                <HelpButton featureKey="template_modal" />
                <ModalCloseButton onClick={handleCloseTemplateModal} />
              </div>
            </div>
            <div className="modal-body">
              {editingTemplate?.isSystem && <SystemContentDisclaimer kind="template" />}
              <div className="form-group">
                <label className={editingTemplate?.isSystem ? "" : "required"}>שם התבנית</label>
                <input type="text" value={templateData.name} onChange={(e) => setTemplateData({ ...templateData, name: e.target.value })} placeholder="לדוגמא: סיכום טיפול ראשון" disabled={!!editingTemplate?.isSystem} />
              </div>
              <div className="form-group">
                <label>שדות התבנית</label>
                <TemplateFieldBuilder fields={templateData.fields} onChange={(fields) => setTemplateData({ ...templateData, fields })} readOnly={!!editingTemplate?.isSystem} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseTemplateModal}>{editingTemplate?.isSystem ? "סגור" : "ביטול"}</button>
              {editingTemplate?.isSystem ? (
                <button className="btn-primary" onClick={() => { handleCloseTemplateModal(); handleDuplicateTemplate(editingTemplate); }} title="שכפל ליצירת עותק אישי שניתן לעריכה">
                  <Copy size={18} weight="regular" />
                  <span>שכפל לעריכה</span>
                </button>
              ) : (
                <>
                  <button className="btn-preview" onClick={handleShowPreview} disabled={!templateData.name.trim()}>
                    <Eye size={18} weight="regular" /><span>תצוגה מקדימה</span>
                  </button>
                  <button className="btn-primary" onClick={handleSaveTemplate} disabled={!templateData.name.trim() || isCreatingTemplate || isUpdatingTemplate}>
                    {(isCreatingTemplate || isUpdatingTemplate) && <LoaderLoop size="xs" />}
                    <span>{editingTemplate ? "שמור שינויים" : "צור תבנית"}</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Template Preview */}
      {showPreview && <TemplatePreview template={templateData} onClose={() => setShowPreview(false)} />}

      {/* Questionnaire Modal */}
      {showQuestionnaireModal && (
        <div className="modal-overlay" onClick={handleCloseQuestionnaireModal}>
          <div className="modal questionnaire-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="tb-modal-title"><span className="tb-modal-icon"><ListBullets size={18} weight="fill" /></span><h3>{questionnairePreviewMode ? `תצוגה מקדימה - ${questionnaireData.name || "שאלון חדש"}` : editingQuestionnaire?.isSystem ? "צפייה בשאלון מובנה" : editingQuestionnaire ? "עריכת שאלון" : "שאלון חדש"}</h3></div>
              <div className="modal-header-actions">
                <HelpButton featureKey="questionnaire_modal" />
                <ModalCloseButton onClick={handleCloseQuestionnaireModal} />
              </div>
            </div>
            <div className="modal-body">
              {questionnairePreviewMode ? (
                <QuestionnairePreviewFields
                  fields={questionnaireData.fields}
                  requireSignature={questionnaireData.requireSignature}
                />
              ) : (
                <>
                  {editingQuestionnaire?.isSystem && <SystemContentDisclaimer kind="questionnaire" />}
                  <div className="form-group">
                    <label className={editingQuestionnaire?.isSystem ? "" : "required"}>שם השאלון</label>
                    <input type="text" value={questionnaireData.name} onChange={(e) => setQuestionnaireData({ ...questionnaireData, name: e.target.value })} placeholder="לדוגמא: שאלון היכרות ראשוני" disabled={!!editingQuestionnaire?.isSystem} dir="rtl" />
                  </div>
                  <div className="form-group">
                    <label>שאלות השאלון</label>
                    <QuestionnaireFieldBuilder fields={questionnaireData.fields} onChange={(fields) => setQuestionnaireData({ ...questionnaireData, fields })} readOnly={!!editingQuestionnaire?.isSystem} />
                  </div>

                  {/* Signature requirement — sits after the questions, like the
                      signature block the patient will see at the bottom */}
                  <div className={`qsig-setting${questionnaireData.requireSignature ? " is-on" : ""}`}>
                    <div className="qsig-setting-icon">
                      <Signature size={18} weight={questionnaireData.requireSignature ? "fill" : "regular"} />
                    </div>
                    <div className="qsig-setting-text">
                      <span className="qsig-setting-title">דרישת חתימה דיגיטלית</span>
                      <span className="qsig-setting-desc">
                        המטופל/ת יחתמו (ביד או בהקלדת שם) בתחתית השאלון, ולא ניתן יהיה לשלוח בלי חתימה. החתימה תופיע בתשובות ובקובץ ה-PDF.
                      </span>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={questionnaireData.requireSignature}
                      aria-label="דרישת חתימה דיגיטלית לשליחת השאלון"
                      className={`qsig-switch${questionnaireData.requireSignature ? " is-on" : ""}`}
                      disabled={!!editingQuestionnaire?.isSystem}
                      onClick={() =>
                        setQuestionnaireData({
                          ...questionnaireData,
                          requireSignature: !questionnaireData.requireSignature,
                        })
                      }
                    >
                      <span className="qsig-switch-knob" />
                    </button>
                  </div>
                  {editingQuestionnaire && !editingQuestionnaire.isSystem && (
                    <p className="qsig-setting-note">
                      השינוי חל על שליחות חדשות בלבד — קישורים שכבר נשלחו למטופלים ממשיכים לפי ההגדרה שהייתה בזמן השליחה.
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer questionnaire-modal-footer">
              {questionnairePreviewMode && (
                <p className="preview-save-notice">שים לב: גם לאחר שמירת השאלון ניתן לערוך אותו בכל עת</p>
              )}
              <div className="questionnaire-footer-actions">
                {questionnairePreviewMode ? (
                  <button className="btn-secondary" onClick={() => setQuestionnairePreviewMode(false)}>
                    <ArrowLeft size={18} weight="bold" />
                    <span>חזרה לעריכה</span>
                  </button>
                ) : (
                  <>
                    <button className="btn-secondary" onClick={handleCloseQuestionnaireModal}>{editingQuestionnaire?.isSystem ? "סגור" : "ביטול"}</button>
                    <button className="btn-secondary" onClick={() => setQuestionnairePreviewMode(true)} disabled={questionnaireData.fields.length === 0}>
                      <Eye size={18} weight="regular" />
                      <span>תצוגה מקדימה</span>
                    </button>
                  </>
                )}
                {editingQuestionnaire?.isSystem ? (
                  <button className="btn-primary" onClick={() => { handleCloseQuestionnaireModal(); handleDuplicateQuestionnaire(editingQuestionnaire); }} title="שכפל ליצירת עותק אישי שניתן לעריכה">
                    <Copy size={18} weight="regular" />
                    <span>שכפל לעריכה</span>
                  </button>
                ) : (
                  <button className="btn-primary" onClick={handleSaveQuestionnaire} disabled={!questionnaireData.name.trim() || questionnaireData.fields.length === 0 || isCreatingQuestionnaire || isUpdatingQuestionnaire}>
                    {(isCreatingQuestionnaire || isUpdatingQuestionnaire) && <LoaderLoop size="xs" />}
                    <span>{editingQuestionnaire ? "שמור שינויים" : "צור שאלון"}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Form Modal */}
      {showSendFormModal && sendingForm && (
        <div className="sm-overlay" onClick={handleCloseSendFormModal}>
          <div className="sm-dialog" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="sm-header">
              <div className="sm-header-right">
                <div className="sm-icon-badge sm-icon-form">
                  <FileText size={20} weight="duotone" />
                </div>
                <div className="sm-header-text">
                  <h3>שליחת טופס לחתימה</h3>
                  <span className="sm-header-subtitle">{sendingForm.name}</span>
                </div>
              </div>
              <ModalCloseButton onClick={handleCloseSendFormModal} />
            </div>

            {/* Body */}
            <div className="sm-body">
              {/* Missing Patient Fields Error */}
              {missingPatientFields.length > 0 && (
                <div className="sm-alert sm-alert-error">
                  <div className="sm-alert-top">
                    <Warning size={16} weight="fill" />
                    <span>חסרים פרטים בתיק המטופל</span>
                  </div>
                  <p className="sm-alert-desc">
                    יש למלא את הפרטים הבאים בתיק המטופל לפני שליחת הטופס:
                  </p>
                  <div className="sm-alert-tags">
                    {missingPatientFields.map(({ field, placeholder }) => (
                      <span key={field} className="sm-alert-tag">{placeholder}</span>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="sm-alert-edit-btn"
                    onClick={() => navigate(`/dashboard/patients/${selectedPatientId}?edit=true`)}
                  >
                    <Pencil size={12} weight="bold" />
                    עריכת תיק המטופל
                  </button>
                </div>
              )}

              {/* Patient Selection */}
              <div className="sm-section">
                <div className="sm-section-top">
                  <label className="sm-label">
                    בחר מטופל <span className="sm-req">*</span>
                  </label>
                  {canToggleClinicPatients && (
                    <div className="sm-toggle-bar">
                      <button
                        type="button"
                        className={`sm-toggle-btn ${!showAllClinicPatients ? "active" : ""}`}
                        onClick={() => { setShowAllClinicPatients(false); setSelectedPatientId(""); }}
                      >
                        <User size={13} weight="bold" />
                        שלי
                      </button>
                      <button
                        type="button"
                        className={`sm-toggle-btn ${showAllClinicPatients ? "active" : ""}`}
                        onClick={() => { setShowAllClinicPatients(true); setSelectedPatientId(""); }}
                      >
                        <UsersThree size={13} weight="bold" />
                        כל הקליניקה
                      </button>
                    </div>
                  )}
                </div>

                <div className="sm-search-box">
                  <MagnifyingGlass size={16} weight="regular" />
                  <input
                    type="text"
                    placeholder="חפש לפי שם או טלפון..."
                    value={patientSearchQuery}
                    onChange={(e) => setPatientSearchQuery(e.target.value)}
                  />
                </div>

                <div className="sm-patient-list">
                  {isLoadingPatients ? (
                    <ContentLoader row size="xs" message="טוען מטופלים..." />
                  ) : filteredPatients.length === 0 ? (
                    <div className="sm-list-status">
                      <Users size={28} weight="duotone" />
                      <span>לא נמצאו מטופלים</span>
                    </div>
                  ) : (
                    filteredPatients.map((patient) => {
                      const isActive = selectedPatientId === patient.id;
                      return (
                        <div
                          key={patient.id}
                          className={`sm-patient-row ${isActive ? "sm-selected" : ""}`}
                          onClick={() => setSelectedPatientId(patient.id)}
                        >
                          <div className="sm-patient-info">
                            <span className="sm-patient-name">
                              {patient.firstName} {patient.lastName}
                            </span>
                            <span className="sm-patient-detail">{patient.phone}</span>
                          </div>
                          {patient.idNumber && (
                            <span className="sm-patient-id-tag">
                              <IdentificationCard size={11} />
                              {patient.idNumber}
                            </span>
                          )}
                          <div className="sm-radio">
                            {isActive && <CheckCircle size={20} weight="fill" />}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* SMS Template */}
              <div className="sm-section">
                <label className="sm-label">תבנית הודעת SMS</label>
                <textarea
                  className="sm-textarea"
                  value={smsTemplate}
                  onChange={(e) => setSmsTemplate(e.target.value)}
                  rows={3}
                  placeholder="הודעת SMS שתישלח למטופל..."
                />
                <div className="sm-help-note">
                  <Info size={13} weight="fill" />
                  <span>
                    משתנים זמינים: {"{name}"} - שם המטופל, {"{formName}"} - שם הטופס, {"{link}"} - קישור לחתימה
                  </span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="sm-footer">
              <button className="sm-cancel" onClick={handleCloseSendFormModal}>
                ביטול
              </button>
              <button
                className="sm-submit"
                onClick={handleSendFormForSignature}
                disabled={!selectedPatientId || isSendingForm || missingPatientFields.length > 0}
              >
                {isSendingForm && <LoaderLoop size="xs" />}
                <PaperPlaneTilt size={16} weight="bold" />
                <span>שלח לחתימה</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Questionnaire Modal */}
      {showSendQuestionnaireModal && sendingQuestionnaire && (
        <div className="sm-overlay" onClick={handleCloseSendQuestionnaireModal}>
          <div className="sm-dialog" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="sm-header">
              <div className="sm-header-right">
                <div className="sm-icon-badge sm-icon-questionnaire">
                  <ListBullets size={20} weight="duotone" />
                </div>
                <div className="sm-header-text">
                  <h3>שליחת שאלון</h3>
                  <span className="sm-header-subtitle">{sendingQuestionnaire.name}</span>
                </div>
              </div>
              <ModalCloseButton onClick={handleCloseSendQuestionnaireModal} />
            </div>

            {/* Body */}
            <div className="sm-body">
              <div className="sm-section">
                <div className="sm-section-top">
                  <label className="sm-label">
                    בחר מטופלים <span className="sm-req">*</span>
                  </label>
                  <div className="sm-multi-actions">
                    <span className="sm-count-chip">
                      <strong>{selectedQuestionnairePatients.length}</strong> נבחרו
                    </span>
                    <button type="button" className="sm-link-btn" onClick={handleSelectAllQuestionnairePatients}>
                      בחר הכל
                    </button>
                    <button type="button" className="sm-link-btn" onClick={handleClearQuestionnairePatients}>
                      נקה
                    </button>
                  </div>
                </div>

                {canToggleClinicPatients && (
                  <div className="sm-toggle-bar">
                    <button
                      type="button"
                      className={`sm-toggle-btn ${!showAllClinicPatients ? "active" : ""}`}
                      onClick={() => { setShowAllClinicPatients(false); setSelectedQuestionnairePatients([]); }}
                    >
                      <User size={13} weight="bold" />
                      שלי
                    </button>
                    <button
                      type="button"
                      className={`sm-toggle-btn ${showAllClinicPatients ? "active" : ""}`}
                      onClick={() => { setShowAllClinicPatients(true); setSelectedQuestionnairePatients([]); }}
                    >
                      <UsersThree size={13} weight="bold" />
                      כל הקליניקה
                    </button>
                  </div>
                )}

                <div className="sm-search-box">
                  <MagnifyingGlass size={16} weight="regular" />
                  <input
                    type="text"
                    placeholder="חפש לפי שם או טלפון..."
                    value={questionnairePatientSearch}
                    onChange={(e) => setQuestionnairePatientSearch(e.target.value)}
                  />
                </div>

                <div className="sm-patient-list sm-multi">
                  {isLoadingPatients ? (
                    <ContentLoader row size="xs" message="טוען מטופלים..." />
                  ) : filteredQuestionnairePatients.length === 0 ? (
                    <div className="sm-list-status">
                      <Users size={28} weight="duotone" />
                      <span>לא נמצאו מטופלים</span>
                    </div>
                  ) : (
                    filteredQuestionnairePatients.map((patient) => {
                      const isSelected = selectedQuestionnairePatients.includes(patient.id);
                      return (
                        <div
                          key={patient.id}
                          className={`sm-patient-row ${isSelected ? "sm-selected" : ""}`}
                          onClick={() => handleToggleQuestionnairePatient(patient.id)}
                        >
                          <div className="sm-checkbox">
                            {isSelected ? (
                              <CheckCircle size={20} weight="fill" />
                            ) : (
                              <div className="sm-empty-check" />
                            )}
                          </div>
                          <div className="sm-patient-info">
                            <span className="sm-patient-name">
                              {patient.firstName} {patient.lastName}
                            </span>
                            <span className="sm-patient-detail">{patient.phone}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="sm-footer">
              <button className="sm-cancel" onClick={handleCloseSendQuestionnaireModal}>
                ביטול
              </button>
              <button
                className="sm-submit"
                onClick={handleSendQuestionnaire}
                disabled={selectedQuestionnairePatients.length === 0 || isSendingQuestionnaire}
              >
                {isSendingQuestionnaire && <LoaderLoop size="xs" />}
                <PaperPlaneTilt size={16} weight="bold" />
                <span>שלח שאלון ({selectedQuestionnairePatients.length})</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        icon={confirmDialog.icon}
        confirmText="מחק"
      />
    </div>
  );
}

export default TasksRemindersPage;
